/**
 * Stage 0 Configuration Store
 *
 * In-memory reactive store for participants, task templates,
 * and availability rules. Provides CRUD operations and change
 * notifications so UI can re-render on mutations.
 */

import {
  applyGroupedPatchToSlot,
  type GroupedConflict,
  type GroupedSlotEdit,
  validateGroupedApply,
} from '../models/grouped-slot-edit';
import {
  type AlgorithmPreset,
  type AlgorithmSettings,
  type AvailabilityWindow,
  type CertificationDefinition,
  type DateUnavailability,
  DEFAULT_ALGORITHM_SETTINGS,
  DEFAULT_CERTIFICATION_DEFINITIONS,
  DEFAULT_PRESET,
  type HardConstraintCode,
  Level,
  LevelEntry,
  type LiveModeState,
  type OneTimeTask,
  type PakalDefinition,
  type Participant,
  type ParticipantSet,
  type ParticipantSnapshot,
  type RestRule,
  type Schedule,
  SchedulerConfig,
  type ScheduleSnapshot,
  type SlotTemplate,
  type SubTeamTemplate,
  type TaskSet,
  type TaskTemplate,
} from '../models/types';
import { jsonDeserialize, jsonSerialize } from '../shared/utils/json-dates';
import { buildFormula } from '../shared/utils/load-formula';
import { clampRestRuleHours } from '../shared/utils/rest-rule-clamp';
import { hourInOpDay } from '../shared/utils/time-utils';
import { clampWorkloadMultiplier } from '../utils/capacity';
import { normalizeCertificationDefinitions, sanitizeColor } from './certification-utils';
import { sanitizeContinuitySnapshotColors } from './continuity-import';
import { buildDefaultContinuityJson } from './default-continuity';
import {
  clonePakalDefinitions,
  DEFAULT_PAKAL_DEFINITIONS,
  HORESH_PAKAL_ID,
  normalizePakalDefinitions,
  sanitizePakalIds,
} from './pakal-utils';

// ─── ID Generation ───────────────────────────────────────────────────────────

let _idCounter = 0;
function uid(prefix: string): string {
  return `${prefix}-${++_idCounter}-${Date.now().toString(36)}`;
}

export { uid };

/**
 * Charset-sanitize a participant id restored from an untrusted blob.
 *
 * Participant ids are normally minted by `uid()` (`p-<counter>-<base36>`, an
 * HTML-safe charset), and the ~dozen raw `data-pid="${p.id}"` sinks across the
 * presentation layer interpolate the id WITHOUT `escAttr` on that standing
 * invariant (see CLAUDE.md: "DOM ids/keys DERIVED from untrusted free text are
 * charset-sanitized at the construction chokepoint, not escaped at the render
 * sink"). A tampered full-backup / schedule-snapshot blob, however, can carry an
 * arbitrary id string (e.g. `p1" onmouseover="…`) that breaks out of those
 * attributes. This guard re-applies the same Unicode letter/number/`_`/`-`
 * whitelist used at the two free-text id *derivation* sites (day0-adapter
 * `foreignParticipant`, `generateTasksFromTemplates`) at the load/import
 * boundary. It is a **no-op for every legitimate `uid()` id**, so it cannot
 * perturb non-tampered data. Determinism is load-bearing: the SAME transform is
 * applied to every id-bearing reference (`assignment.participantId`,
 * `scheduleUnavailability`/`capabilityLoss` `participantId`, notWith pid/targets)
 * so references stay consistent for a tampered blob WITHOUT an explicit id-remap
 * — a collision (two crafted ids slugging to one value) only ever drops a
 * tampered participant, never a legitimate one.
 */
export function sanitizeParticipantId(id: unknown): string {
  return typeof id === 'string' ? id.replace(/[^\p{L}\p{N}_-]/gu, '_') : '';
}

/**
 * Re-coerce every participant-id-bearing field on a frozen schedule through
 * `sanitizeParticipantId`. The id counterpart of the snapshot color guard: the
 * active schedule (`loadSchedule`) and every library snapshot (`_sanitizeSnapshots`)
 * can be planted verbatim by a tampered full-backup / snapshot import, and their
 * frozen participants feed the raw `data-pid`
 * board/sidebar sinks. No-op for legit `uid()` ids, deterministic, so the
 * participants and the assignments/unavailability/cap-loss that reference them
 * stay consistent.
 */
function _sanitizeScheduleParticipantIds(schedule: Schedule | null | undefined): void {
  if (!schedule) return;
  for (const p of schedule.participants ?? []) {
    p.id = sanitizeParticipantId(p.id);
    if (p.notWithIds) p.notWithIds = p.notWithIds.map(sanitizeParticipantId);
  }
  for (const a of schedule.assignments ?? []) a.participantId = sanitizeParticipantId(a.participantId);
  for (const su of schedule.scheduleUnavailability ?? []) su.participantId = sanitizeParticipantId(su.participantId);
  for (const cl of schedule.capabilityLoss ?? []) cl.participantId = sanitizeParticipantId(cl.participantId);
}

// ─── Save Error Handler / Storage Health ────────────────────────────────────

/**
 * Persistence errors are funnelled through `reportSaveError()` so the UI sees
 * them at most once per `SAVE_ERROR_TOAST_COOLDOWN_MS`, and so that a single
 * quota failure latches `_storageWedged = true`. While wedged, the **debounced
 * auto-save** (`saveToStorage()` via `debouncedSave()`) is skipped — preventing
 * the toast flood that used to fire on every downstream mutation once the
 * quota was exhausted.
 *
 * Explicit user-triggered writes (saveSchedule, _saveSnapshots, _savePresets,
 * …) are still attempted regardless of the wedge, because they may be *smaller*
 * than the blob they replace (e.g., a snapshot delete) and would succeed,
 * clearing the wedge via `onSaveSuccess()`.
 */
let _onSaveError: ((err: unknown, info: { isQuota: boolean }) => void) | null = null;
export function setSaveErrorHandler(handler: (err: unknown, info: { isQuota: boolean }) => void): void {
  _onSaveError = handler;
}

const SAVE_ERROR_TOAST_COOLDOWN_MS = 15_000;
let _lastSaveErrorAt = 0;
let _storageWedged = false;

/**
 * True if the last persistence attempt exhausted the browser's localStorage
 * quota. While wedged, `debouncedSave()` is a no-op until a save succeeds
 * (e.g., after the user deletes snapshots). UI code can call this to surface
 * a prominent banner.
 */
export function isStorageWedged(): boolean {
  return _storageWedged;
}

/**
 * Recognise a "quota exceeded" error across browsers:
 *  - Chrome / modern Safari: `DOMException` with `name === 'QuotaExceededError'` (code 22)
 *  - Firefox:                `DOMException` with `name === 'NS_ERROR_DOM_QUOTA_REACHED'` (code 1014)
 *  - Old WebKit:             `name === 'QUOTA_EXCEEDED_ERR'`
 *  - Safari private mode:    plain `Error` whose message contains "quota"
 */
function isQuotaExceededError(err: unknown): boolean {
  if (!err) return false;
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    if (err.code === 22 || err.code === 1014) return true;
    if (
      err.name === 'QuotaExceededError' ||
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err.name === 'QUOTA_EXCEEDED_ERR'
    )
      return true;
  }
  const msg = (err as { message?: unknown })?.message;
  if (typeof msg === 'string' && /quota|storage/i.test(msg)) return true;
  return false;
}

function reportSaveError(err: unknown): void {
  const isQuota = isQuotaExceededError(err);
  if (isQuota) _storageWedged = true;

  const now = Date.now();
  if (now - _lastSaveErrorAt < SAVE_ERROR_TOAST_COOLDOWN_MS) return;
  _lastSaveErrorAt = now;
  _onSaveError?.(err, { isQuota });
}

function onSaveSuccess(): void {
  if (_storageWedged) {
    _storageWedged = false;
    console.log('[Store] Storage quota recovered — resuming persistence.');
  }
}

/**
 * Diagnostic: returns the approximate byte size of every persistence key this
 * module owns, plus the grand total. Useful for an in-app "storage usage"
 * inspector or for debugging the "storage full" symptom.
 */
export function getStorageUsage(): { key: string; bytes: number }[] & { total: number } {
  const keys = [
    STORAGE_KEY_STATE,
    STORAGE_KEY_SCHEDULE,
    STORAGE_KEY_LIVE_MODE,
    STORAGE_KEY_ALGORITHM,
    STORAGE_KEY_PRESETS,
    STORAGE_KEY_ACTIVE_PRESET,
    STORAGE_KEY_SNAPSHOTS,
    STORAGE_KEY_ACTIVE_SNAPSHOT,
    STORAGE_KEY_PSETS,
    STORAGE_KEY_ACTIVE_PSET,
    STORAGE_KEY_TASK_SETS,
    STORAGE_KEY_ACTIVE_TASK_SET,
  ];
  const rows = keys.map((key) => {
    let bytes = 0;
    try {
      const raw = localStorage.getItem(key);
      // UTF-16 code units ≈ 2 bytes each; approximate since most chars are ASCII.
      if (raw) bytes = key.length + raw.length;
    } catch {
      bytes = 0;
    }
    return { key, bytes };
  }) as { key: string; bytes: number }[] & { total: number };
  rows.total = rows.reduce((s, r) => s + r.bytes, 0);
  return rows;
}

// ─── Listener System ─────────────────────────────────────────────────────────

type Listener = () => void;
const listeners: Set<Listener> = new Set();

/**
 * Monotonic counter bumped on every master-data mutation (i.e. every
 * non-suppressed notify()). Stamped onto a Schedule at generation as
 * `Schedule.masterDataRevision` so a post-generation edit to participants /
 * task templates / one-time tasks can be detected across a page reload — the
 * in-session `_scheduleDirty` flag in app.ts is never persisted, and the
 * id/sourceName-only orphan check (`scheduleHasOrphans`) misses both content
 * edits (certs/level/availability) and additions of brand-new entities.
 *
 * Persisted INSIDE the version-7 STORAGE_KEY_STATE blob (see saveToStorage /
 * loadFromStorage): it round-trips as a single integer (no serialization-order
 * fragility) and — crucially — persists ATOMICALLY with the master data it
 * tracks, since the same saveToStorage() write carries both. The debounce can
 * therefore never persist the counter and the data out of step.
 *
 * Algorithm-settings changes ride a separate channel (notifyAlgorithmChanged /
 * STORAGE_KEY_ALGORITHM) and deliberately do NOT bump this counter; that
 * staleness axis stays in-session only across a reload, exactly as before.
 */
let _masterDataRevision = 0;

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Current master-data revision (see `_masterDataRevision`). */
export function getMasterDataRevision(): number {
  return _masterDataRevision;
}

function notify(): void {
  // Inside a transaction() the listeners + persist are coalesced to a single
  // notify() fired by the outermost transaction (see `transaction`).
  if (_suppressNotify) return;
  // A real (non-suppressed) master-data mutation: advance the revision so the
  // active schedule can be flagged stale after a reload. Placed after the
  // suppress guard so a transaction() batch counts as one revision and undo/
  // redo (which call notify() once) advance it like any other mutation.
  _masterDataRevision++;
  for (const fn of listeners) {
    try {
      fn();
    } catch (err) {
      console.error('[Store] Listener threw:', err);
    }
  }
  // Auto-persist state to localStorage (debounced)
  debouncedSave();
}

// ─── Algorithm-change listeners (lightweight — no undo/reconciliation) ───────

const _algoListeners: Set<Listener> = new Set();

/**
 * Subscribe to algorithm-setting mutations (weights, HC toggles, presets).
 * Unlike the main `subscribe()`, this does NOT trigger schedule reconciliation
 * or undo snapshots — it only lets the UI mark the schedule as stale.
 */
export function subscribeAlgorithmChange(fn: Listener): () => void {
  _algoListeners.add(fn);
  return () => _algoListeners.delete(fn);
}

function notifyAlgorithmChanged(): void {
  for (const fn of _algoListeners) {
    try {
      fn();
    } catch (err) {
      console.error('[Store] Algorithm listener threw:', err);
    }
  }
}

// ─── Undo / Redo System ──────────────────────────────────────────────────────

interface StoreSnapshot {
  participants: Array<{ p: Participant; dateUnavails: DateUnavailability[] }>;
  taskTemplates: TaskTemplate[];
  oneTimeTasks: OneTimeTask[];
  notWithPairs: Array<[string, string[]]>;
  pakalDefinitions: PakalDefinition[];
  certificationDefinitions: CertificationDefinition[];
  restRules: RestRule[];
}

const MAX_HISTORY = 80;
const undoStack: StoreSnapshot[] = [];
const redoStack: StoreSnapshot[] = [];
let _suppressSnapshot = false;
let _suppressNotify = false;
let _txnDepth = 0;

/**
 * Capture the current state as a deep-cloned snapshot.
 *
 * Uses structuredClone when available (all modern browsers & Node 17+)
 * for a single C++ pass instead of manually spreading every field.
 * Falls back to a manual clone only for environments that lack it.
 */
function captureSnapshot(): StoreSnapshot {
  const useStructured = typeof structuredClone === 'function';
  const ps: StoreSnapshot['participants'] = [];
  for (const [id, p] of participants) {
    // Date objects survive structuredClone; avoid per-field spreading
    const clonedP = useStructured
      ? structuredClone(p)
      : {
          ...p,
          certifications: [...p.certifications],
          availability: p.availability.map((w) => ({
            start: new Date(w.start.getTime()),
            end: new Date(w.end.getTime()),
          })),
          dateUnavailability: [...(p.dateUnavailability || [])].map((r) => ({ ...r })),
        };
    const rawDus = dateUnavailabilities.get(id) || [];
    ps.push({
      p: clonedP,
      dateUnavails: useStructured ? structuredClone(rawDus) : rawDus.map((r) => ({ ...r })),
    });
  }
  const tpls: TaskTemplate[] = useStructured
    ? structuredClone([...taskTemplates.values()])
    : [...taskTemplates.values()].map((tpl) => ({
        ...tpl,
        loadWindows: (tpl.loadWindows || []).map((w) => ({ ...w })),
        slots: tpl.slots.map((s) => ({
          ...s,
          acceptableLevels: [...s.acceptableLevels],
          requiredCertifications: [...s.requiredCertifications],
          forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
        })),
        subTeams: tpl.subTeams.map((st) => ({
          ...st,
          slots: st.slots.map((s) => ({
            ...s,
            acceptableLevels: [...s.acceptableLevels],
            requiredCertifications: [...s.requiredCertifications],
            forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
          })),
        })),
      }));
  const ots: OneTimeTask[] = useStructured
    ? structuredClone([...oneTimeTasks.values()])
    : [...oneTimeTasks.values()].map((ot) => ({
        ...ot,
        scheduledDate: new Date(ot.scheduledDate.getTime()),
        loadWindows: (ot.loadWindows || []).map((w) => ({ ...w })),
        slots: ot.slots.map((s) => ({
          ...s,
          acceptableLevels: [...s.acceptableLevels],
          requiredCertifications: [...s.requiredCertifications],
          forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
        })),
        subTeams: ot.subTeams.map((st) => ({
          ...st,
          slots: st.slots.map((s) => ({
            ...s,
            acceptableLevels: [...s.acceptableLevels],
            requiredCertifications: [...s.requiredCertifications],
            forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
          })),
        })),
      }));
  const nwPairs: Array<[string, string[]]> = [];
  for (const [pid, set] of notWithPairs) {
    nwPairs.push([pid, [...set]]);
  }
  return {
    participants: ps,
    taskTemplates: tpls,
    oneTimeTasks: ots,
    notWithPairs: nwPairs,
    pakalDefinitions: clonePakalDefinitions(pakalDefinitions),
    certificationDefinitions: certificationDefinitions.map((d) => ({ ...d })),
    restRules: _restRules.map((r) => ({ ...r })),
  };
}

/** Replace live state with a snapshot (restoring original IDs). */
function restoreSnapshot(snap: StoreSnapshot): void {
  participants.clear();
  dateUnavailabilities.clear();
  notWithPairs.clear();

  const useStructured = typeof structuredClone === 'function';

  for (const entry of snap.participants) {
    const p: Participant = useStructured
      ? structuredClone(entry.p)
      : {
          ...entry.p,
          certifications: [...entry.p.certifications],
          availability: entry.p.availability.map((w) => ({
            start: new Date(w.start.getTime()),
            end: new Date(w.end.getTime()),
          })),
          dateUnavailability: (entry.dateUnavails || []).map((r) => ({ ...r })),
        };
    if (!useStructured) {
      p.dateUnavailability = (entry.dateUnavails || []).map((r) => ({ ...r }));
    }
    participants.set(p.id, p);
    if (entry.dateUnavails && entry.dateUnavails.length > 0) {
      dateUnavailabilities.set(
        p.id,
        useStructured ? structuredClone(entry.dateUnavails) : entry.dateUnavails.map((r) => ({ ...r })),
      );
    }
  }

  // Ensure participant inline dateUnavailability shares the same array
  // reference as the dateUnavailabilities Map entry.
  for (const [id, p] of participants) {
    p.dateUnavailability = dateUnavailabilities.get(id) || [];
  }

  // Restore notWithPairs and sync to participant objects
  if (snap.notWithPairs) {
    for (const [pid, targets] of snap.notWithPairs) {
      notWithPairs.set(pid, new Set(targets));
    }
  }
  syncNotWithToParticipants();

  pakalDefinitions = normalizePakalDefinitions(snap.pakalDefinitions);
  certificationDefinitions = snap.certificationDefinitions.map((d) => ({ ...d }));
  for (const p of participants.values()) {
    p.pakalIds = sanitizePakalIds(p.pakalIds, pakalDefinitions);
  }

  // Recompute availability from canonical data — the snapshot does not
  // capture scheduleDate/scheduleDays, so restored availability windows
  // may be stale relative to the current scheduling window.
  recalcAllAvailability();

  taskTemplates.clear();
  if (useStructured) {
    for (const tpl of snap.taskTemplates) {
      taskTemplates.set(tpl.id, structuredClone(tpl));
    }
  } else {
    for (const tpl of snap.taskTemplates) {
      taskTemplates.set(tpl.id, {
        ...tpl,
        loadWindows: (tpl.loadWindows || []).map((w) => ({ ...w })),
        slots: tpl.slots.map((s) => ({
          ...s,
          acceptableLevels: [...s.acceptableLevels],
          requiredCertifications: [...s.requiredCertifications],
          forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
        })),
        subTeams: tpl.subTeams.map((st) => ({
          ...st,
          slots: st.slots.map((s) => ({
            ...s,
            acceptableLevels: [...s.acceptableLevels],
            requiredCertifications: [...s.requiredCertifications],
            forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
          })),
        })),
      });
    }
  }

  oneTimeTasks.clear();
  if (useStructured) {
    for (const ot of snap.oneTimeTasks || []) {
      oneTimeTasks.set(ot.id, structuredClone(ot));
    }
  } else {
    for (const ot of snap.oneTimeTasks || []) {
      oneTimeTasks.set(ot.id, {
        ...ot,
        scheduledDate: new Date(ot.scheduledDate.getTime()),
        loadWindows: (ot.loadWindows || []).map((w) => ({ ...w })),
        slots: ot.slots.map((s) => ({
          ...s,
          acceptableLevels: [...s.acceptableLevels],
          requiredCertifications: [...s.requiredCertifications],
          forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
        })),
        subTeams: ot.subTeams.map((st) => ({
          ...st,
          slots: st.slots.map((s) => ({
            ...s,
            acceptableLevels: [...s.acceptableLevels],
            requiredCertifications: [...s.requiredCertifications],
            forbiddenCertifications: s.forbiddenCertifications ? [...s.forbiddenCertifications] : undefined,
          })),
        })),
      });
    }
  }

  _restRules = (snap.restRules || []).map((r) => ({ ...r }));
}

/**
 * Save current state to the undo stack before a mutation.
 * Called at the START of every mutating operation.
 */
function pushSnapshot(): void {
  if (_suppressSnapshot) return;
  undoStack.push(captureSnapshot());
  if (undoStack.length > MAX_HISTORY) undoStack.shift();
  // Any new mutation clears the redo future
  redoStack.length = 0;
}

/**
 * Run `fn` as a single atomic store mutation: one undo snapshot captured before
 * it runs, and one `notify()` (listeners + persist) fired after it completes.
 *
 * `fn` may call any number of ordinary exported mutations (updateParticipant,
 * setTaskNamePreference, addNotWith, add/update/removeDateUnavailability, …);
 * their inner pushSnapshot()/notify() become no-ops for the duration, so the
 * whole batch is one undo step and triggers exactly one schedule reconciliation
 * (one structuredClone in app.ts onStoreChanged) instead of N.
 *
 * Re-entrant: only the outermost transaction snapshots and notifies. On a thrown
 * error the snapshot is still in place (undo restores the pre-call state) and a
 * single notify() still fires; there is no partial rollback, so callers should
 * validate before mutating (matching bulkMutateParticipants).
 */
export function transaction<T>(fn: () => T): T {
  const outer = _txnDepth === 0;
  if (outer) pushSnapshot();
  _txnDepth++;
  const prevSuppressSnapshot = _suppressSnapshot;
  const prevSuppressNotify = _suppressNotify;
  _suppressSnapshot = true;
  _suppressNotify = true;
  try {
    return fn();
  } finally {
    _suppressSnapshot = prevSuppressSnapshot;
    _suppressNotify = prevSuppressNotify;
    _txnDepth--;
    if (outer) notify();
  }
}

/** Undo the last action. Returns true if successful. */
export function undo(): boolean {
  if (undoStack.length === 0) return false;
  // Save current state to redo before restoring
  redoStack.push(captureSnapshot());
  const snap = undoStack.pop()!;
  restoreSnapshot(snap);
  notify();
  return true;
}

/** Redo the last undone action. Returns true if successful. */
export function redo(): boolean {
  if (redoStack.length === 0) return false;
  // Save current state to undo before restoring
  undoStack.push(captureSnapshot());
  const snap = redoStack.pop()!;
  restoreSnapshot(snap);
  notify();
  return true;
}

/** Query undo/redo availability for UI button states. */
export function getUndoRedoState(): { canUndo: boolean; canRedo: boolean; undoDepth: number; redoDepth: number } {
  return {
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoDepth: undoStack.length,
    redoDepth: redoStack.length,
  };
}

/**
 * Push a no-op checkpoint onto the undo stack for actions that live outside
 * the store (e.g. swap-picker commits mutate the schedule directly through
 * the engine). The snapshot captures the current — unchanged — store state,
 * so restoring it during undo is a no-op; its sole purpose is to enable the
 * header undo/redo buttons so the parallel schedule undo stack in app.ts
 * stays wired to them. Does NOT call notify() — callers are expected to
 * manage their own schedule-level snapshot and refresh.
 */
export function pushUndoCheckpoint(): void {
  pushSnapshot();
}

// ─── Participant Store ───────────────────────────────────────────────────────

const participants: Map<string, Participant> = new Map();
const dateUnavailabilities: Map<string, DateUnavailability[]> = new Map(); // participantId -> rules
const notWithPairs: Map<string, Set<string>> = new Map(); // participantId -> set of partner IDs
let pakalDefinitions: PakalDefinition[] = clonePakalDefinitions(DEFAULT_PAKAL_DEFINITIONS);

// ─── Certification Definitions ──────────────────────────────────────────────

let certificationDefinitions: CertificationDefinition[] = DEFAULT_CERTIFICATION_DEFINITIONS.map((d) => ({ ...d }));

export function getCertificationDefinitions(): CertificationDefinition[] {
  return certificationDefinitions.filter((d) => !d.deleted).map((d) => ({ ...d }));
}

export function getCertificationById(id: string): CertificationDefinition | undefined {
  const def = certificationDefinitions.find((d) => d.id === id);
  return def ? { ...def } : undefined;
}

export function getCertLabel(certId: string): string {
  return certificationDefinitions.find((d) => d.id === certId)?.label ?? certId;
}

export function getCertColor(certId: string): string {
  return certificationDefinitions.find((d) => d.id === certId)?.color ?? '#7f8c8d';
}

export function addCertification(label: string, color: string): CertificationDefinition {
  const trimmed = label.trim();
  if (!trimmed) throw new Error('Certification label cannot be empty');
  if (trimmed.length > 100) throw new Error('Certification label too long (max 50 characters)');
  if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) throw new Error('Invalid hex color');
  if (certificationDefinitions.some((d) => !d.deleted && d.label === trimmed)) {
    throw new Error(`הסמכה "${trimmed}" כבר קיימת`);
  }
  pushSnapshot();
  const def: CertificationDefinition = { id: uid('cert'), label: trimmed, color };
  certificationDefinitions.push(def);
  notify();
  return { ...def };
}

export function renameCertification(id: string, label: string): string | null {
  const trimmed = label.trim().replace(/\s+/g, ' ');
  if (!trimmed) return 'שם הסמכה לא יכול להיות ריק';
  if (trimmed.length > 100) return 'שם הסמכה ארוך מדי (מקסימום 100 תווים)';
  const def = certificationDefinitions.find((d) => d.id === id && !d.deleted);
  if (!def) return 'הסמכה לא נמצאה';
  if (def.label === trimmed) return null;
  const duplicate = certificationDefinitions.find(
    (d) => d.id !== id && !d.deleted && d.label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (duplicate) return 'הסמכה כזאת כבר קיימת';
  pushSnapshot();
  def.label = trimmed;
  notify();
  return null;
}

export function updateCertificationColor(id: string, color: string): void {
  if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) throw new Error('Invalid hex color');
  const def = certificationDefinitions.find((d) => d.id === id && !d.deleted);
  if (!def) return;
  if (def.color === color) return;
  pushSnapshot();
  def.color = color;
  notify();
}

export function removeCertification(id: string): void {
  const def = certificationDefinitions.find((d) => d.id === id && !d.deleted);
  if (!def) return;
  pushSnapshot();
  def.deleted = true;
  notify();
}

export function getCertificationUsage(id: string): { participantCount: number; slotCount: number } {
  let participantCount = 0;
  let slotCount = 0;
  for (const p of participants.values()) {
    if (p.certifications.includes(id)) participantCount++;
  }
  for (const tpl of taskTemplates.values()) {
    for (const s of [...tpl.slots, ...tpl.subTeams.flatMap((st) => st.slots)]) {
      if (s.requiredCertifications.includes(id) || s.forbiddenCertifications?.includes(id)) slotCount++;
    }
  }
  for (const ot of oneTimeTasks.values()) {
    for (const s of [...ot.slots, ...ot.subTeams.flatMap((st) => st.slots)]) {
      if (s.requiredCertifications.includes(id) || s.forbiddenCertifications?.includes(id)) slotCount++;
    }
  }
  return { participantCount, slotCount };
}

function normalizePakalLabel(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

export function ensurePakalDefinitions(definitions: PakalDefinition[]): void {
  pakalDefinitions = normalizePakalDefinitions([...pakalDefinitions, ...definitions]);
  for (const participant of participants.values()) {
    participant.pakalIds = sanitizePakalIds(participant.pakalIds, pakalDefinitions);
  }
}

export function ensureCertificationDefinitions(definitions: CertificationDefinition[]): void {
  certificationDefinitions = normalizeCertificationDefinitions([...certificationDefinitions, ...definitions]);
}

export function getPakalDefinitions(): PakalDefinition[] {
  return clonePakalDefinitions(pakalDefinitions.filter((d) => !d.deleted));
}

export function getAllPakalDefinitionsIncludeDeleted(): PakalDefinition[] {
  return clonePakalDefinitions(pakalDefinitions);
}

export function getPakalById(id: string): PakalDefinition | undefined {
  const def = pakalDefinitions.find((d) => d.id === id);
  return def ? { ...def } : undefined;
}

export function getPakalLabel(pakalId: string): string {
  return pakalDefinitions.find((d) => d.id === pakalId)?.label ?? pakalId;
}

export function addPakal(label: string): { definition?: PakalDefinition; error?: string } {
  const normalizedLabel = normalizePakalLabel(label);
  if (!normalizedLabel) return { error: 'שם פק"ל לא יכול להיות ריק' };
  const duplicate = pakalDefinitions.find(
    (def) => !def.deleted && def.label.toLowerCase() === normalizedLabel.toLowerCase(),
  );
  if (duplicate) return { error: 'פק"ל כזה כבר קיים' };

  pushSnapshot();
  const definition: PakalDefinition = {
    id: uid('pakal'),
    label: normalizedLabel,
  };
  pakalDefinitions = [...pakalDefinitions, definition];
  notify();
  return { definition };
}

export function renamePakal(id: string, label: string): string | null {
  const normalizedLabel = normalizePakalLabel(label);
  if (!normalizedLabel) return 'שם פק"ל לא יכול להיות ריק';
  const definition = pakalDefinitions.find((def) => def.id === id && !def.deleted);
  if (!definition) return 'פק"ל לא נמצא';

  const duplicate = pakalDefinitions.find(
    (def) => def.id !== id && !def.deleted && def.label.toLowerCase() === normalizedLabel.toLowerCase(),
  );
  if (duplicate) return 'פק"ל כזה כבר קיים';

  pushSnapshot();
  definition.label = normalizedLabel;
  notify();
  return null;
}

export function getPakalUsageCount(id: string): number {
  let count = 0;
  for (const participant of participants.values()) {
    const selected = new Set(sanitizePakalIds(participant.pakalIds, pakalDefinitions));
    if (selected.has(id)) count += 1;
  }
  return count;
}

export function removePakal(id: string): void {
  const def = pakalDefinitions.find((d) => d.id === id && !d.deleted);
  if (!def) return;
  pushSnapshot();
  def.deleted = true;
  notify();
}

/** Maximum allowed `dayIndex` / `endDayIndex` for a DateUnavailability rule.
 *  The active scheduleDays setting clamps further at evaluation time; we keep
 *  7 as the storage-level ceiling so rules remain valid across scheduleDays
 *  changes. */
const MAX_RULE_DAY_INDEX = 7;

function normalizeDateUnavailabilityRule(
  rule: Partial<Omit<DateUnavailability, 'id'>> | null | undefined,
): Omit<DateUnavailability, 'id'> | null {
  if (!rule) return null;

  if (typeof rule.dayIndex !== 'number' || !Number.isFinite(rule.dayIndex)) return null;
  const dayIndex = Math.trunc(rule.dayIndex);
  if (!Number.isInteger(dayIndex) || dayIndex < 1 || dayIndex > MAX_RULE_DAY_INDEX) return null;

  let endDayIndex: number | undefined;
  if (rule.endDayIndex !== undefined && rule.endDayIndex !== null) {
    const v = Math.trunc(rule.endDayIndex as number);
    if (!Number.isInteger(v) || v < 1 || v > MAX_RULE_DAY_INDEX || v < dayIndex) return null;
    if (v !== dayIndex) endDayIndex = v;
  }

  const allDay = !!rule.allDay;
  const startHour = allDay ? 0 : Math.trunc(rule.startHour ?? 0);
  const endHour = allDay ? 24 : Math.trunc(rule.endHour ?? 0);
  if (!allDay && (startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23)) return null;

  return {
    dayIndex,
    ...(endDayIndex !== undefined ? { endDayIndex } : {}),
    allDay,
    startHour,
    endHour,
    reason: rule.reason?.trim() || undefined,
  };
}

/** Default schedule date: next upcoming Sunday from today. */
function defaultScheduleDate(): Date {
  const now = new Date();
  const day = now.getDay();
  const daysUntilSunday = day === 0 ? 0 : 7 - day;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilSunday);
}
let scheduleDate: Date = defaultScheduleDate();
/**
 * Storage-level ceiling for the schedule length (and for any day-indexed config
 * that must survive scheduleDays changes — DateUnavailability rules, one-time
 * task days). The active `scheduleDays` clamps to this; day-indexed config is
 * stored up to this ceiling so it stays valid when the period is shortened then
 * re-extended.
 */
export const MAX_SCHEDULE_DAYS = 7;
let scheduleDays: number = MAX_SCHEDULE_DAYS;

export function getScheduleDate(): Date {
  return scheduleDate;
}
export function getScheduleDays(): number {
  return scheduleDays;
}
export function setScheduleDate(d: Date): void {
  pushSnapshot();
  scheduleDate = d;
  recalcAllAvailability();
  notify();
}
export function setScheduleDays(n: number): void {
  pushSnapshot();
  scheduleDays = Math.max(1, Math.min(MAX_SCHEDULE_DAYS, n));
  recalcAllAvailability();
  notify();
}

/**
 * Return a freshly-built default Day 0 continuity snapshot, anchored at
 * the current `scheduleDate` and bound to the active default rest rule's
 * id (so HC-14 cross-boundary pairing fires).  Returns `null` when no
 * rest rule is available (defaults haven't been seeded yet).
 *
 * Consumed by `app.ts` on first launch to pre-populate the continuity
 * buffer; the snapshot becomes part of `Schedule.continuitySnapshot`
 * after the user generates a schedule.
 */
export function getDefaultContinuityJson(): string | null {
  const rule = _restRules.find((r) => !r.deleted);
  if (!rule) return null;
  return buildDefaultContinuityJson(scheduleDate, rule.id);
}

// ─── HC-14 Rest Rules ───────────────────────────────────────────────────────

let _restRules: RestRule[] = [];

/** Return all non-deleted rest rules. */
export function getRestRules(): RestRule[] {
  return _restRules.filter((r) => !r.deleted);
}

/** Return all rest rules including soft-deleted tombstones (for orphan display). */
export function getAllRestRules(): RestRule[] {
  return _restRules;
}

/** Look up a rest rule by ID (includes tombstones). */
export function getRestRuleById(id: string): RestRule | undefined {
  return _restRules.find((r) => r.id === id);
}

/**
 * Ids of soft-deleted rest rules (tombstones). The complement of
 * `buildRestRuleMap`'s key set within the catalog: an id here was deliberately
 * turned off by the user, so HC-14 must stay off for it. Fed to
 * `SchedulingEngine.setPhantomContext` so a continuity snapshot cannot revive
 * it — see `mergePhantomRules` for why "absent from the live map" is not a
 * sufficient test on its own.
 */
export function getDeletedRestRuleIds(): Set<string> {
  const ids = new Set<string>();
  for (const r of _restRules) {
    if (r.deleted) ids.add(r.id);
  }
  return ids;
}

/** Create a new rest rule. Returns the created rule. */
export function addRestRule(label: string, durationHours: number): RestRule {
  pushSnapshot();
  const rule: RestRule = {
    id: uid('rr'),
    label: label.trim(),
    durationHours: Math.max(0.5, Math.min(24, durationHours)),
  };
  _restRules.push(rule);
  notify();
  return rule;
}

/** Update an existing non-deleted rest rule. */
export function updateRestRule(id: string, updates: { label?: string; durationHours?: number }): void {
  const rule = _restRules.find((r) => r.id === id && !r.deleted);
  if (!rule) return;
  pushSnapshot();
  if (updates.label !== undefined) rule.label = updates.label.trim();
  if (updates.durationHours !== undefined) rule.durationHours = Math.max(0.5, Math.min(24, updates.durationHours));
  notify();
}

/** Soft-delete a rest rule. Tasks referencing it become orphans. */
export function removeRestRule(id: string): void {
  const rule = _restRules.find((r) => r.id === id && !r.deleted);
  if (!rule) return;
  pushSnapshot();
  rule.deleted = true;
  notify();
}

/**
 * Clamp a rest-rule duration (hours) to the same [0.5, 24] range the UI input
 * enforces, NaN-safe. Defense-in-depth at the engine-consumption boundary:
 * `addRestRule`/`updateRestRule` clamp on the UI path, but the import /
 * snapshot-restore / localStorage load paths copy `durationHours` verbatim
 * (`loadTaskSet`, `restoreSnapshot`, `loadFromStorage`), so a crafted or
 * tampered value can reach here. HC-14 reads this map (and the frozen
 * `restRuleSnapshot` is built from it via `Object.fromEntries`): a duration
 * <= 0 makes `gap < durationMs` always false (HC-14 silently disabled), while a
 * non-finite / huge value makes it always true (every rest-ruled pair flagged).
 * We clamp rather than drop the entry — dropping would also disable HC-14 for
 * that rule. Mirrors the defensive guard in `computeAvailability`. Identity for
 * any already-valid duration ⇒ zero regression.
 *
 * Delegates to the shared `clampRestRuleHours` so this boundary and the engine's
 * phantom chokepoint (`buildPhantomContext`) — the two independent writers into
 * `SchedulingEngine.restRuleMap` — cannot drift on the ceiling.
 */
function sanitizeRestDurationHours(h: number): number {
  return clampRestRuleHours(h); // NaN → 8; 0 / negatives → 0.5; ±Infinity / >24 → 24
}

/** Build a Map<ruleId, durationMs> for non-deleted rules (engine consumption). */
export function buildRestRuleMap(): Map<string, number> {
  const map = new Map<string, number>();
  for (const r of _restRules) {
    if (!r.deleted) map.set(r.id, sanitizeRestDurationHours(r.durationHours) * 3600000);
  }
  return map;
}

/**
 * Build the engine's rest-rule map from a frozen `restRuleSnapshot` record
 * (millisecond durations), clamping each entry through the same guard as
 * `buildRestRuleMap`. This is the snapshot/load twin of `buildRestRuleMap`: a
 * loaded `Schedule` (snapshot import, localStorage restore, day-continuity
 * export) constructs the engine's map straight from `restRuleSnapshot` and never
 * passes through `buildRestRuleMap`, so a crafted/tampered snapshot could
 * otherwise smuggle a poisoned duration (<= 0 or non-finite) past HC-14.
 * Idempotent for any self-produced snapshot (already built via `buildRestRuleMap`)
 * ⇒ identity.
 */
export function sanitizeRestRuleSnapshot(snapshot: Record<string, number> | undefined | null): Map<string, number> {
  const map = new Map<string, number>();
  if (!snapshot) return map;
  for (const [id, ms] of Object.entries(snapshot)) {
    map.set(id, sanitizeRestDurationHours(Number(ms) / 3600000) * 3600000);
  }
  return map;
}

function getDefaultAvailability(): AvailabilityWindow[] {
  const d = scheduleDate;
  const dsh = getDayStartHour();
  // Cover the full multi-day window plus a buffer sized to the LATEST possible
  // task end, so no legal template chain is unfillable for everyone.
  //
  // A task anchored on the last op-day (op-day `scheduleDays`) can run up to 24h
  // from its start, and the latest a task can start inside that op-day is just
  // under its end boundary (`base + scheduleDays days @ dsh`) — the post-midnight
  // tail (`startHour < dsh`). So the latest possible task end is strictly before
  // `base + (scheduleDays + 1) days @ dsh`; ending the window there guarantees
  // every legal chain (clamped to shifts×dur ≤ 24, startHour ≤ 23) is coverable.
  //
  // A tighter noon ceiling (the former `+scheduleDays days @ 12:00`) left any
  // late-anchored last-day chain that crossed noon — e.g. 14:00 + 3×8h (ends
  // 14:00 the next day) — uncovered for EVERY participant, so preflight
  // (`checkPerShiftAvailability`) hard-blocked generation with a misleading
  // "no participants available" Critical the user could not act on (there is no
  // UI to widen availability; `computeAvailability` only subtracts blackouts).
  // This mirrors how interior op-days already behave — an op-day's tasks may
  // physically spill into the next op-day's early hours. The capacity denominator
  // is unaffected: `computeParticipantCapacity`/`checkCapacity` clip to their own
  // `[base@dsh, base+numDays@dsh)` window, so the extra tail hours are not counted.
  return [
    {
      start: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0),
      end: new Date(d.getFullYear(), d.getMonth(), d.getDate() + scheduleDays + 1, dsh, 0),
    },
  ];
}

function computeAvailability(participantId: string): AvailabilityWindow[] {
  const full = getDefaultAvailability();
  const dateRules = dateUnavailabilities.get(participantId) || [];

  // Expand recurring schedule-day rules into concrete blackout-style windows.
  //
  // Semantics: a rule references schedule days by index (`dayIndex`, optionally
  // extended with `endDayIndex`). The blackout is composed in op-day space via
  // `hourInOpDay`, so hours below `dayStartHour` land on the post-midnight tail
  // of the referenced op-day.
  //   - allDay: `[opDayStart(startIdx), opDayStart(endIdx+1))`
  //   - partial: `[hourInOpDay(startIdx, startHour), hourInOpDay(endIdx, endHour))`,
  //              with a 24h end-wrap when end <= start on the same day.
  // Rules outside 1..scheduleDays are clamped and never contribute a blackout
  // beyond the window — matches the engine's evaluator.
  const expandedBlackouts: Array<{ start: Date; end: Date }> = [];

  const schedStart = scheduleDate;
  const hBoundary = getDayStartHour();
  const baseY = schedStart.getFullYear();
  const baseM = schedStart.getMonth();
  const baseD = schedStart.getDate();
  for (const rule of dateRules) {
    // Defensive guard: skip malformed rules rather than letting NaN math
    // produce an Invalid-Date blackout (which would wipe the whole window).
    if (!Number.isInteger(rule.dayIndex)) continue;
    const startIdx = Math.max(1, rule.dayIndex);
    const endIdx = Math.min(scheduleDays, rule.endDayIndex ?? rule.dayIndex);
    if (endIdx < 1 || startIdx > scheduleDays || endIdx < startIdx) continue;

    if (rule.allDay) {
      expandedBlackouts.push({
        start: new Date(baseY, baseM, baseD + startIdx - 1, hBoundary, 0),
        end: new Date(baseY, baseM, baseD + endIdx, hBoundary, 0),
      });
    } else {
      const startMs = hourInOpDay(schedStart, hBoundary, startIdx, rule.startHour);
      let endMs = hourInOpDay(schedStart, hBoundary, endIdx, rule.endHour);
      if (endMs <= startMs) endMs += 24 * 3600 * 1000;
      expandedBlackouts.push({ start: new Date(startMs), end: new Date(endMs) });
    }
  }

  if (expandedBlackouts.length === 0) return full;

  // Subtract all blackout windows from the full availability
  let windows = [...full];
  for (const bout of expandedBlackouts) {
    const next: AvailabilityWindow[] = [];
    for (const w of windows) {
      if (bout.end <= w.start || bout.start >= w.end) {
        // No overlap
        next.push(w);
      } else {
        // Overlap — split
        if (bout.start > w.start) {
          next.push({ start: w.start, end: bout.start });
        }
        if (bout.end < w.end) {
          next.push({ start: bout.end, end: w.end });
        }
      }
    }
    windows = next;
  }
  return windows;
}

function recalcAllAvailability(): void {
  for (const [id, p] of participants) {
    p.availability = computeAvailability(id);
  }
}

/** Case-insensitive trimmed name duplicate check for participants. */
export function isParticipantNameTaken(name: string, excludeId?: string): boolean {
  const norm = name.trim().toLowerCase();
  if (!norm) return false;
  for (const p of participants.values()) {
    if (p.id === excludeId) continue;
    if (p.name.trim().toLowerCase() === norm) return true;
  }
  return false;
}

// ── Internal no-snapshot helpers (used by public functions & bulkMutateParticipants) ──

export const MAX_PARTICIPANT_NAME_LENGTH = 30;

function _addParticipantNoSnapshot(data: {
  name: string;
  level?: Level;
  certifications?: string[];
  pakalIds?: string[];
  group: string;
  workloadMultiplier?: number;
}): Participant {
  const id = uid('p');
  const firstActiveId = certificationDefinitions.find((d) => !d.deleted)?.id;
  const certs = data.certifications ?? (firstActiveId ? [firstActiveId] : []);
  const pIds = sanitizePakalIds(data.pakalIds, pakalDefinitions);
  const p: Participant = {
    id,
    name: data.name.slice(0, MAX_PARTICIPANT_NAME_LENGTH),
    level: data.level ?? Level.L0,
    certifications: certs,
    pakalIds: pIds,
    group: data.group,
    availability: getDefaultAvailability(),
    dateUnavailability: [],
  };
  if (data.workloadMultiplier !== undefined && data.workloadMultiplier !== 1) {
    p.workloadMultiplier = data.workloadMultiplier;
  }
  participants.set(id, p);
  return p;
}

function _updateParticipantNoSnapshot(id: string, patch: Partial<Omit<Participant, 'id' | 'availability'>>): void {
  const p = participants.get(id);
  if (!p) return;
  const nextPatch = { ...patch };
  if (typeof nextPatch.name === 'string') {
    nextPatch.name = nextPatch.name.slice(0, MAX_PARTICIPANT_NAME_LENGTH);
  }
  // biome-ignore lint/suspicious/noPrototypeBuiltins: ES2020 target doesn't support Object.hasOwn
  if (Object.prototype.hasOwnProperty.call(nextPatch, 'pakalIds')) {
    nextPatch.pakalIds = sanitizePakalIds(nextPatch.pakalIds, pakalDefinitions);
  }
  // Replace the stored Participant object rather than mutating in place. This
  // keeps the store's update path local: any external reference (engine,
  // schedule, undo stack) that happens to be holding the previous object
  // observes its old, unmutated state. The engine and schedule already
  // deep-clone on entry / generation, so this is purely defensive.
  const next: Participant = { ...p, ...nextPatch };
  next.availability = computeAvailability(id);
  participants.set(id, next);
}

function _removeParticipantNoSnapshot(id: string): void {
  participants.delete(id);
  dateUnavailabilities.delete(id);
  cleanupNotWith(id);
}

function _setTaskNamePreferenceNoSnapshot(pid: string, preferred?: string, lessPreferred?: string): void {
  const p = participants.get(pid);
  if (!p) return;
  p.preferredTaskName = preferred;
  p.lessPreferredTaskName = lessPreferred;
}

// ── Public CRUD (delegate to helpers, wrapping with snapshot + notify) ──

export function addParticipant(data: {
  name: string;
  level?: Level;
  certifications?: string[];
  pakalIds?: string[];
  group: string;
  workloadMultiplier?: number;
}): Participant {
  pushSnapshot();
  const p = _addParticipantNoSnapshot(data);
  notify();
  return p;
}

export function updateParticipant(id: string, patch: Partial<Omit<Participant, 'id' | 'availability'>>): void {
  const p = participants.get(id);
  if (!p) return;
  pushSnapshot();
  _updateParticipantNoSnapshot(id, patch);
  notify();
}

export function removeParticipant(id: string): void {
  if (!participants.has(id)) return;
  pushSnapshot();
  _removeParticipantNoSnapshot(id);
  syncNotWithToParticipants();
  notify();
}

/**
 * Remove multiple participants and all their associated data in one
 * undo-able action.  Uses a Set for O(1) membership checks and deletes
 * participants and unavailability entries in a single pass.
 * Returns the number of participants actually removed.
 */
export function removeParticipantsBulk(ids: string[]): number {
  const removeSet = new Set(ids);
  const validIds: string[] = [];
  for (const id of removeSet) {
    if (participants.has(id)) validIds.push(id);
  }
  if (validIds.length === 0) return 0;
  pushSnapshot();
  for (const id of validIds) {
    participants.delete(id);
    dateUnavailabilities.delete(id);
    cleanupNotWith(id);
  }
  syncNotWithToParticipants();
  notify();
  return validIds.length;
}

export function getParticipant(id: string): Participant | undefined {
  return participants.get(id);
}

export function getAllParticipants(): Participant[] {
  return [...participants.values()];
}

export function getGroups(): string[] {
  const groups = new Set<string>();
  for (const p of participants.values()) groups.add(p.group);
  return [...groups].sort();
}

// ─── Date Unavailability Management ──────────────────────────────────────────

export function addDateUnavailability(
  participantId: string,
  rule: Omit<DateUnavailability, 'id'>,
): DateUnavailability | null {
  if (!participants.has(participantId)) return null;
  const normalized = normalizeDateUnavailabilityRule(rule);
  if (!normalized) return null;
  pushSnapshot();
  const du: DateUnavailability = { ...normalized, id: uid('du') };
  const arr = dateUnavailabilities.get(participantId) || [];
  arr.push(du);
  dateUnavailabilities.set(participantId, arr);
  const p = participants.get(participantId)!;
  p.dateUnavailability = arr;
  p.availability = computeAvailability(participantId);
  notify();
  return du;
}

export function removeDateUnavailability(participantId: string, ruleId: string): void {
  const arr = dateUnavailabilities.get(participantId);
  if (!arr) return;
  const idx = arr.findIndex((r) => r.id === ruleId);
  if (idx < 0) return;
  pushSnapshot();
  arr.splice(idx, 1);
  const p = participants.get(participantId);
  if (p) {
    p.dateUnavailability = arr;
    p.availability = computeAvailability(participantId);
  }
  notify();
}

/**
 * Update an existing DateUnavailability rule in place, preserving its id and
 * position in the participant's list. Returns the updated rule, or null if the
 * participant/rule is missing or the new values fail validation.
 *
 * Use this instead of remove + re-add so editing a block stays a single undo
 * step (and inside a transaction(), a single atomic mutation) without churning
 * the rule id or list order.
 */
export function updateDateUnavailability(
  participantId: string,
  ruleId: string,
  rule: Omit<DateUnavailability, 'id'>,
): DateUnavailability | null {
  const arr = dateUnavailabilities.get(participantId);
  if (!arr) return null;
  const idx = arr.findIndex((r) => r.id === ruleId);
  if (idx < 0) return null;
  const normalized = normalizeDateUnavailabilityRule(rule);
  if (!normalized) return null;
  pushSnapshot();
  const updated: DateUnavailability = { ...normalized, id: ruleId };
  arr.splice(idx, 1, updated);
  const p = participants.get(participantId);
  if (p) {
    p.dateUnavailability = arr;
    p.availability = computeAvailability(participantId);
  }
  notify();
  return updated;
}

export function getDateUnavailabilities(participantId: string): DateUnavailability[] {
  return dateUnavailabilities.get(participantId) || [];
}

/**
 * Add the same DateUnavailability rule to multiple participants in one
 * undo-able action.  Returns the count of successfully added entries.
 */
export function addDateUnavailabilityBulk(participantIds: string[], rule: Omit<DateUnavailability, 'id'>): number {
  const normalized = normalizeDateUnavailabilityRule(rule);
  if (!normalized) return 0;
  const validIds = participantIds.filter((id) => participants.has(id));
  if (validIds.length === 0) return 0;
  pushSnapshot();
  _suppressSnapshot = true;
  let count = 0;
  try {
    for (const pid of validIds) {
      const du: DateUnavailability = { ...normalized, id: uid('du') };
      const arr = dateUnavailabilities.get(pid) || [];
      arr.push(du);
      dateUnavailabilities.set(pid, arr);
      const p = participants.get(pid)!;
      p.dateUnavailability = arr;
      p.availability = computeAvailability(pid);
      count++;
    }
  } finally {
    _suppressSnapshot = false;
  }
  notify();
  return count;
}

// ─── Not-With Preference Management ─────────────────────────────────────────

/** Sync the notWithPairs map to each participant's notWithIds field. */
function syncNotWithToParticipants(): void {
  for (const p of participants.values()) {
    const set = notWithPairs.get(p.id);
    p.notWithIds = set && set.size > 0 ? [...set] : undefined;
  }
}

export function addNotWith(pidA: string, pidB: string): void {
  if (pidA === pidB) return;
  if (!participants.has(pidA) || !participants.has(pidB)) return;
  // Check if already exists
  const setA = notWithPairs.get(pidA);
  if (setA?.has(pidB)) return;
  pushSnapshot();
  // Symmetric add
  if (!notWithPairs.has(pidA)) notWithPairs.set(pidA, new Set());
  if (!notWithPairs.has(pidB)) notWithPairs.set(pidB, new Set());
  notWithPairs.get(pidA)!.add(pidB);
  notWithPairs.get(pidB)!.add(pidA);
  syncNotWithToParticipants();
  notify();
}

export function removeNotWith(pidA: string, pidB: string): void {
  const setA = notWithPairs.get(pidA);
  const setB = notWithPairs.get(pidB);
  if (!setA?.has(pidB)) return;
  pushSnapshot();
  setA.delete(pidB);
  if (setA.size === 0) notWithPairs.delete(pidA);
  if (setB) {
    setB.delete(pidA);
    if (setB.size === 0) notWithPairs.delete(pidB);
  }
  syncNotWithToParticipants();
  notify();
}

export function getNotWithIds(pid: string): string[] {
  const set = notWithPairs.get(pid);
  return set ? [...set] : [];
}

/** Remove a participant from all notWithPairs entries. */
function cleanupNotWith(pid: string): void {
  const set = notWithPairs.get(pid);
  if (set) {
    for (const partnerId of set) {
      const partnerSet = notWithPairs.get(partnerId);
      if (partnerSet) {
        partnerSet.delete(pid);
        if (partnerSet.size === 0) notWithPairs.delete(partnerId);
      }
    }
    notWithPairs.delete(pid);
  }
}

// ─── Task Preference Accessors ──────────────────────────────────────────────

/** Get a participant's task name preferences. */
export function getTaskNamePreference(pid: string): { preferred?: string; lessPreferred?: string } {
  const p = participants.get(pid);
  if (!p) return {};
  return { preferred: p.preferredTaskName, lessPreferred: p.lessPreferredTaskName };
}

/** Set a participant's task name preferences. Pass undefined to clear. */
export function setTaskNamePreference(pid: string, preferred?: string, lessPreferred?: string): void {
  const p = participants.get(pid);
  if (!p) return;
  if (p.preferredTaskName === preferred && p.lessPreferredTaskName === lessPreferred) return;
  pushSnapshot();
  _setTaskNamePreferenceNoSnapshot(pid, preferred, lessPreferred);
  notify();
}

// ─── Bulk Participant Mutations ─────────────────────────────────────────────

export interface BulkParticipantOp {
  type: 'add' | 'update' | 'delete';
  /** For 'update' and 'delete': existing participant ID */
  id?: string;
  /** For 'add' and 'update': the field values */
  data?: {
    name: string;
    group: string;
    level?: Level;
    certifications?: string[];
    pakalIds?: string[];
    preferredTaskName?: string;
    lessPreferredTaskName?: string;
    workloadMultiplier?: number;
  };
}

/**
 * Apply multiple participant add/update/delete operations in one undo-able action.
 * A single snapshot is pushed before all mutations, and notify() is called once at the end.
 */
export function bulkMutateParticipants(ops: BulkParticipantOp[]): { added: number; updated: number; deleted: number } {
  if (ops.length === 0) return { added: 0, updated: 0, deleted: 0 };
  pushSnapshot();
  _suppressSnapshot = true;
  let added = 0;
  let updated = 0;
  let deleted = 0;
  try {
    for (const op of ops) {
      switch (op.type) {
        case 'add': {
          const p = _addParticipantNoSnapshot(op.data!);
          if (op.data!.preferredTaskName || op.data!.lessPreferredTaskName) {
            _setTaskNamePreferenceNoSnapshot(p.id, op.data!.preferredTaskName, op.data!.lessPreferredTaskName);
          }
          added++;
          break;
        }
        case 'update': {
          const existing = participants.get(op.id!);
          if (existing) {
            _updateParticipantNoSnapshot(op.id!, op.data!);
            _setTaskNamePreferenceNoSnapshot(op.id!, op.data!.preferredTaskName, op.data!.lessPreferredTaskName);
            updated++;
          }
          break;
        }
        case 'delete': {
          if (participants.has(op.id!)) {
            _removeParticipantNoSnapshot(op.id!);
            deleted++;
          }
          break;
        }
      }
    }
    syncNotWithToParticipants();
  } finally {
    _suppressSnapshot = false;
  }
  notify();
  return { added, updated, deleted };
}

// ─── Task Template Store ─────────────────────────────────────────────────────

const taskTemplates: Map<string, TaskTemplate> = new Map();

/**
 * Clamp task-template numeric fields to valid ranges.
 * Returns a sanitized shallow copy of only the numeric fields present in `raw`.
 *   durationHours  → 0.5 … min(24, 24/shiftsPerDay)   (NaN → 8)
 *   shiftsPerDay   → 1 … floor(24/durationHours)      (NaN → 1, rounded to integer)
 *   startHour      → 0 … 23                           (NaN → 6, rounded to integer)
 *
 * `shiftsPerDay * durationHours` must not exceed 24h (a single op-day). When
 * that product would overflow, `options.clampSide` decides which field gets
 * clamped down: 'shiftsPerDay' (default) treats duration as the user's intent
 * and shrinks the shift count; 'durationHours' does the inverse. `fallback*`
 * provide the "other" field's value when it isn't in `raw` (e.g. a partial
 * patch from `updateTaskTemplate`).
 */
export function sanitizeTemplateNumericFields<
  T extends Partial<Pick<TaskTemplate, 'durationHours' | 'shiftsPerDay' | 'startHour'>>,
>(
  raw: T,
  options: {
    clampSide?: 'shiftsPerDay' | 'durationHours';
    fallbackDurationHours?: number;
    fallbackShiftsPerDay?: number;
  } = {},
): T {
  const out = { ...raw };
  if (out.startHour !== undefined) {
    let v = Number(out.startHour);
    if (Number.isNaN(v)) v = 6;
    out.startHour = Math.max(0, Math.min(23, Math.round(v)));
  }

  const clampSide = options.clampSide ?? 'shiftsPerDay';
  const hasDur = out.durationHours !== undefined;
  const hasShifts = out.shiftsPerDay !== undefined;

  if (clampSide === 'shiftsPerDay') {
    // Sanitize duration first to its own range, then cap shifts by it.
    let durEff: number;
    if (hasDur) {
      durEff = Number(out.durationHours);
      if (Number.isNaN(durEff)) durEff = 8;
      durEff = Math.max(0.5, Math.min(24, durEff));
      out.durationHours = durEff;
    } else {
      durEff = options.fallbackDurationHours ?? 8;
      if (Number.isNaN(durEff)) durEff = 8;
      durEff = Math.max(0.5, Math.min(24, durEff));
    }
    if (hasShifts) {
      let v = Number(out.shiftsPerDay);
      if (Number.isNaN(v)) v = 1;
      const dynMax = Math.max(1, Math.floor(24 / durEff));
      out.shiftsPerDay = Math.max(1, Math.min(dynMax, Math.round(v)));
    }
  } else {
    // clampSide === 'durationHours'. Sanitize shifts first (1..48 absolute),
    // then cap duration by it. 48 = floor(24 / 0.5), the most shifts that fit.
    let shiftsEff: number;
    if (hasShifts) {
      shiftsEff = Number(out.shiftsPerDay);
      if (Number.isNaN(shiftsEff)) shiftsEff = 1;
      shiftsEff = Math.max(1, Math.min(48, Math.round(shiftsEff)));
      out.shiftsPerDay = shiftsEff;
    } else {
      shiftsEff = options.fallbackShiftsPerDay ?? 1;
      if (Number.isNaN(shiftsEff)) shiftsEff = 1;
      shiftsEff = Math.max(1, Math.min(48, Math.round(shiftsEff)));
    }
    if (hasDur) {
      let v = Number(out.durationHours);
      if (Number.isNaN(v)) v = 8;
      const dynMax = Math.min(24, 24 / shiftsEff);
      out.durationHours = Math.max(0.5, Math.min(dynMax, v));
    }
  }

  return out;
}

function cloneLoadFormula(f: TaskTemplate['loadFormula']): TaskTemplate['loadFormula'] {
  if (!f) return undefined;
  const sc = (globalThis as { structuredClone?: <T>(v: T) => T }).structuredClone;
  if (typeof sc === 'function') return sc(f);
  return {
    computedValue: f.computedValue,
    computedAt: f.computedAt,
    targetHours: f.targetHours,
    components: f.components.map((c) => ({ ...c, refRate: { ...c.refRate } })),
    snapshot: f.snapshot.map((s) => ({ ...s, rate: { ...s.rate } })),
    lhsExtras: f.lhsExtras?.map((c) => ({ ...c, refRate: { ...c.refRate } })),
    lhsExtrasSnapshot: f.lhsExtrasSnapshot?.map((s) => ({ ...s, rate: { ...s.rate } })),
  };
}

/**
 * Next `displayOrder` for a newly created task type: one past the current max
 * across all task templates and one-time tasks, so new types sort after the
 * existing ones in creation order instead of all colliding at the default
 * (`?? 100`) fallback. Explicitly-provided values (e.g. the seeded defaults)
 * are preserved by the callers' `?? nextTaskDisplayOrder()` guard.
 *
 * Note: both template and one-time `displayOrder` are frozen onto each
 * generated task (`Task.displayOrder`) and feed the schedule board's section
 * sort via `deriveSectionVisual` in `layout-engine.ts`.
 */
/**
 * Sanitize a possibly-tainted color on data loaded from storage / imported files.
 * Preserves an absent color (so templates without an explicit color still get an
 * auto-assigned one downstream) while coercing any present string through the
 * hex-only {@link sanitizeColor} guard. See sanitizeColor for the threat model.
 */
function _safeColor(color: string | undefined): string | undefined {
  return typeof color === 'string' ? sanitizeColor(color) : color;
}

function nextTaskDisplayOrder(): number {
  let max = -1;
  for (const t of taskTemplates.values()) {
    if (typeof t.displayOrder === 'number' && t.displayOrder > max) max = t.displayOrder;
  }
  for (const t of oneTimeTasks.values()) {
    if (typeof t.displayOrder === 'number' && t.displayOrder > max) max = t.displayOrder;
  }
  return max + 1;
}

/**
 * Normalize a new template spec into a full `TaskTemplate` with a fresh id.
 * Pure w.r.t. the store as long as the spec supplies `color` and `displayOrder`
 * (the fallbacks read the live store) — the built-in task-set builders rely on that.
 */
function _materializeTemplate(tpl: Omit<TaskTemplate, 'id'>): TaskTemplate {
  const id = uid('tpl');
  const sanitized = sanitizeTemplateNumericFields(tpl);
  return {
    ...sanitized,
    id,
    color: sanitized.color || getNextAvailableColor(),
    baseLoadWeight: sanitized.baseLoadWeight ?? 1,
    splittable: sanitized.splittable ?? false,
    displayOrder: sanitized.displayOrder ?? nextTaskDisplayOrder(),
    loadFormula: cloneLoadFormula(sanitized.loadFormula),
    loadWindows: (sanitized.loadWindows || []).map((w) => ({
      ...w,
      loadFormula: cloneLoadFormula(w.loadFormula),
    })),
  };
}

export function addTaskTemplate(tpl: Omit<TaskTemplate, 'id'>): TaskTemplate {
  pushSnapshot();
  const full = _materializeTemplate(tpl);
  taskTemplates.set(full.id, full);
  notify();
  return full;
}

export function updateTaskTemplate(id: string, patch: Partial<Omit<TaskTemplate, 'id'>>): void {
  const tpl = taskTemplates.get(id);
  if (!tpl) return;
  pushSnapshot();
  const sanitized = sanitizeTemplateNumericFields(patch, {
    fallbackDurationHours: tpl.durationHours,
    fallbackShiftsPerDay: tpl.shiftsPerDay,
  });
  patch = sanitized;
  Object.assign(tpl, patch);
  // HC-15: when shiftsPerDay shrinks, trim stale indices in the rule's
  // triggerShifts. If trimming would empty the set, drop the rule entirely
  // so the user has to re-enable and re-pick on the new shift count.
  if (tpl.sleepRecovery && tpl.shiftsPerDay >= 1) {
    const trimmed = tpl.sleepRecovery.triggerShifts.filter((idx) => idx >= 1 && idx <= tpl.shiftsPerDay);
    if (trimmed.length === 0) {
      tpl.sleepRecovery = undefined;
    } else if (trimmed.length !== tpl.sleepRecovery.triggerShifts.length) {
      tpl.sleepRecovery = { ...tpl.sleepRecovery, triggerShifts: trimmed };
    }
  }
  if (patch.loadWindows) {
    tpl.loadWindows = patch.loadWindows.map((w) => ({
      ...w,
      loadFormula: cloneLoadFormula(w.loadFormula),
    }));
  }
  if (patch.baseLoadWeight !== undefined) {
    tpl.baseLoadWeight = Math.max(0, Math.min(1, patch.baseLoadWeight));
  }
  // Invariant: if a formula was set in the patch, base weight must match its computedValue.
  if ('loadFormula' in patch) {
    if (patch.loadFormula) {
      const cloned = cloneLoadFormula(patch.loadFormula);
      tpl.loadFormula = cloned;
      tpl.baseLoadWeight = cloned!.computedValue;
    } else {
      tpl.loadFormula = undefined;
    }
  } else if (
    patch.baseLoadWeight !== undefined &&
    tpl.loadFormula &&
    Math.abs(tpl.baseLoadWeight! - tpl.loadFormula.computedValue) > 1e-9
  ) {
    // Manual edit to baseLoadWeight that diverges from the formula → drop formula.
    tpl.loadFormula = undefined;
  }
  notify();
}

/**
 * Rename a task template and propagate the new name to every participant
 * preference (`preferredTaskName` / `lessPreferredTaskName`) that referenced
 * the old name. Single snapshot so undo restores both sides atomically.
 * No-op when the trimmed name is unchanged.
 */
export function renameTaskTemplate(id: string, newName: string): void {
  const tpl = taskTemplates.get(id);
  if (!tpl) return;
  const trimmed = newName.trim();
  const oldName = tpl.name.trim();
  if (!trimmed || trimmed === oldName) return;
  pushSnapshot();
  tpl.name = trimmed;
  for (const p of participants.values()) {
    if (p.preferredTaskName?.trim() === oldName) p.preferredTaskName = trimmed;
    if (p.lessPreferredTaskName?.trim() === oldName) p.lessPreferredTaskName = trimmed;
  }
  notify();
}

export function removeTaskTemplate(id: string): void {
  if (!taskTemplates.has(id)) return;
  pushSnapshot();
  taskTemplates.delete(id);
  notify();
}

export function getTaskTemplate(id: string): TaskTemplate | undefined {
  return taskTemplates.get(id);
}

export function getAllTaskTemplates(): TaskTemplate[] {
  return [...taskTemplates.values()];
}

// ─── Config-Derived Helpers (task-name-agnostic) ───────────────────────────

/** Auto-palette for templates that lack an explicit color. */
const AUTO_PALETTE = [
  '#3498db',
  '#e74c3c',
  '#f39c12',
  '#27ae60',
  '#8e44ad',
  '#1abc9c',
  '#e67e22',
  '#34495e',
  '#16a085',
  '#c0392b',
];

/**
 * Return the next available unique color for a new task.
 * Prefers AUTO_PALETTE colors not currently used by any template or one-time task.
 * When the palette is exhausted, generates a new HSL-based color that is visually
 * distinct from all currently used colors.
 */
export function getNextAvailableColor(): string {
  const usedColors = new Set<string>();
  for (const tpl of taskTemplates.values()) {
    if (tpl.color) usedColors.add(tpl.color.toLowerCase());
  }
  for (const ot of oneTimeTasks.values()) {
    if (ot.color) usedColors.add(ot.color.toLowerCase());
  }

  // Try palette first
  for (const c of AUTO_PALETTE) {
    if (!usedColors.has(c.toLowerCase())) return c;
  }

  // Palette exhausted — generate a distinct HSL color
  const usedHues = [...usedColors].map((hex) => hexToHue(hex));
  let bestHue = 0;
  let bestDist = -1;
  for (let h = 0; h < 360; h += 5) {
    const minDist = usedHues.reduce((min, uh) => {
      const d = Math.min(Math.abs(h - uh), 360 - Math.abs(h - uh));
      return Math.min(min, d);
    }, 360);
    if (minDist > bestDist) {
      bestDist = minDist;
      bestHue = h;
    }
  }
  return hslToHex(bestHue, 65, 55);
}

function hexToHue(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h = 0;
  if (max === r) h = ((g - b) / d + 6) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return Math.round(h * 60);
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Build a template-name → visual attributes map.
 * Keyed by template `name` (= `sourceName` on Task). Used by ui-helpers /
 * tab-profile for per-template badge rendering — unrelated to section grouping.
 */
export function getTemplateVisualMap(): Record<string, { color: string; displayOrder: number }> {
  const map: Record<string, { color: string; displayOrder: number }> = {};
  let autoIdx = 0;
  for (const tpl of taskTemplates.values()) {
    if (!map[tpl.name]) {
      map[tpl.name] = {
        color: tpl.color || AUTO_PALETTE[autoIdx++ % AUTO_PALETTE.length],
        displayOrder: tpl.displayOrder ?? 100,
      };
    }
  }
  return map;
}

// ─── One-Time Task Store ────────────────────────────────────────────────────

const oneTimeTasks: Map<string, OneTimeTask> = new Map();

export function addOneTimeTask(task: Omit<OneTimeTask, 'id'>): OneTimeTask {
  pushSnapshot();
  const id = uid('ot');
  const full: OneTimeTask = {
    ...task,
    id,
    color: task.color || getNextAvailableColor(),
    baseLoadWeight: task.baseLoadWeight ?? 1,
    displayOrder: task.displayOrder ?? nextTaskDisplayOrder(),
    loadWindows: (task.loadWindows || []).map((w) => ({ ...w })),
  };
  oneTimeTasks.set(id, full);
  notify();
  return full;
}

export function updateOneTimeTask(id: string, patch: Partial<Omit<OneTimeTask, 'id'>>): void {
  const ot = oneTimeTasks.get(id);
  if (!ot) return;
  pushSnapshot();
  Object.assign(ot, patch);
  if (patch.loadWindows) {
    ot.loadWindows = patch.loadWindows.map((w) => ({ ...w }));
  }
  if (patch.baseLoadWeight !== undefined) {
    ot.baseLoadWeight = Math.max(0, Math.min(1, patch.baseLoadWeight));
  }
  notify();
}

export function removeOneTimeTask(id: string): void {
  if (!oneTimeTasks.has(id)) return;
  pushSnapshot();
  oneTimeTasks.delete(id);
  notify();
}

export function getOneTimeTask(id: string): OneTimeTask | undefined {
  return oneTimeTasks.get(id);
}

export function getAllOneTimeTasks(): OneTimeTask[] {
  return [...oneTimeTasks.values()];
}

// ─── Task duplication ────────────────────────────────────────────────────────

/**
 * Deep clone that preserves Date values (OneTimeTask.scheduledDate). structuredClone
 * is already relied on at runtime (see restoreSnapshot); the JSON fallback exists only
 * for environments without it and would stringify Dates — never reached in practice.
 */
function _cloneDeep<T>(v: T): T {
  const sc = (globalThis as { structuredClone?: <U>(x: U) => U }).structuredClone;
  return typeof sc === 'function' ? sc(v) : (JSON.parse(JSON.stringify(v)) as T);
}

/** Regenerate EVERY nested id in place on an already-cloned task body. */
function _regenerateNestedTaskIds(t: TaskTemplate | OneTimeTask): void {
  for (const s of t.slots) s.id = uid('slot');
  for (const st of t.subTeams) {
    st.id = uid('st');
    for (const s of st.slots) s.id = uid('slot');
  }
  if (t.loadWindows) for (const w of t.loadWindows) w.id = uid('lw');
}

/** Build a "<base> (עותק)" name, escalating to "(עותק 2)" etc. while `taken` is true. */
function _uniqueTaskName(base: string, taken: (n: string) => boolean): string {
  let name = `${base} (עותק)`;
  let attempt = 2;
  while (taken(name)) name = `${base} (עותק ${attempt++})`;
  return name;
}

/**
 * Duplicate a task template into a fully independent copy: new top-level id, every
 * nested id (slots / sub-teams / load windows) regenerated, a unique "(עותק)" name,
 * a fresh palette color, and placement at the end of the list. Shared references
 * (restRuleId, certification ids, load-formula refs to OTHER templates) are preserved.
 * Single undo step (one addTaskTemplate call). Returns null if the source is missing.
 */
export function duplicateTaskTemplate(id: string): TaskTemplate | null {
  const source = getTaskTemplate(id);
  if (!source) return null;
  const clone = _cloneDeep(source);
  _regenerateNestedTaskIds(clone);
  const { id: _omit, ...body } = clone;
  body.name = _uniqueTaskName(source.name.trim(), (n) =>
    getAllTaskTemplates().some((t) => t.name.trim().toLowerCase() === n.toLowerCase()),
  );
  body.color = undefined; // fresh palette color
  body.displayOrder = undefined; // sort at end
  return addTaskTemplate(body);
}

/** As {@link duplicateTaskTemplate}, for one-time tasks (preserves scheduledDate). */
export function duplicateOneTimeTask(id: string): OneTimeTask | null {
  const source = getOneTimeTask(id);
  if (!source) return null;
  const clone = _cloneDeep(source);
  _regenerateNestedTaskIds(clone);
  const { id: _omit, ...body } = clone;
  body.name = _uniqueTaskName(source.name.trim(), (n) =>
    getAllOneTimeTasks().some((t) => t.name.trim().toLowerCase() === n.toLowerCase()),
  );
  body.color = undefined;
  body.displayOrder = undefined;
  return addOneTimeTask(body);
}

// ─── Slot / Sub-Team helpers ─────────────────────────────────────────────────

export function addSlotToTemplate(templateId: string, slot: Omit<SlotTemplate, 'id'>): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  pushSnapshot();
  tpl.slots.push({ ...slot, id: uid('slot') });
  notify();
}

export function removeSlotFromTemplate(templateId: string, slotId: string): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  pushSnapshot();
  tpl.slots = tpl.slots.filter((s) => s.id !== slotId);
  notify();
}

export function addSubTeamToTemplate(templateId: string, name: string): SubTeamTemplate {
  const tpl = taskTemplates.get(templateId);
  const st: SubTeamTemplate = { id: uid('st'), name, slots: [] };
  if (tpl) {
    pushSnapshot();
    tpl.subTeams.push(st);
    notify();
  }
  return st;
}

export function removeSubTeamFromTemplate(templateId: string, subTeamId: string): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  pushSnapshot();
  tpl.subTeams = tpl.subTeams.filter((s) => s.id !== subTeamId);
  notify();
}

export function addSlotToSubTeam(templateId: string, subTeamId: string, slot: Omit<SlotTemplate, 'id'>): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  const st = tpl.subTeams.find((s) => s.id === subTeamId);
  if (!st) return;
  pushSnapshot();
  st.slots.push({ ...slot, id: uid('slot') });
  notify();
}

export function removeSlotFromSubTeam(templateId: string, subTeamId: string, slotId: string): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  const st = tpl.subTeams.find((s) => s.id === subTeamId);
  if (!st) return;
  pushSnapshot();
  st.slots = st.slots.filter((s) => s.id !== slotId);
  notify();
}

// ─── One-Time Task Slot / Sub-Team helpers ──────────────────────────────────

export function addSlotToOneTimeTask(otId: string, slot: Omit<SlotTemplate, 'id'>): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  pushSnapshot();
  ot.slots.push({ ...slot, id: uid('slot') });
  notify();
}

export function removeSlotFromOneTimeTask(otId: string, slotId: string): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  pushSnapshot();
  ot.slots = ot.slots.filter((s) => s.id !== slotId);
  notify();
}

export function addSubTeamToOneTimeTask(otId: string, name: string): SubTeamTemplate {
  const ot = oneTimeTasks.get(otId);
  const st: SubTeamTemplate = { id: uid('st'), name, slots: [] };
  if (ot) {
    pushSnapshot();
    ot.subTeams.push(st);
    notify();
  }
  return st;
}

export function removeSubTeamFromOneTimeTask(otId: string, subTeamId: string): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  pushSnapshot();
  ot.subTeams = ot.subTeams.filter((s) => s.id !== subTeamId);
  notify();
}

export function addSlotToOneTimeSubTeam(otId: string, subTeamId: string, slot: Omit<SlotTemplate, 'id'>): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  const st = ot.subTeams.find((s) => s.id === subTeamId);
  if (!st) return;
  pushSnapshot();
  st.slots.push({ ...slot, id: uid('slot') });
  notify();
}

export function removeSlotFromOneTimeSubTeam(otId: string, subTeamId: string, slotId: string): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  const st = ot.subTeams.find((s) => s.id === subTeamId);
  if (!st) return;
  pushSnapshot();
  st.slots = st.slots.filter((s) => s.id !== slotId);
  notify();
}

// ─── Slot Update helpers ─────────────────────────────────────────────────────

type SlotPatch = Omit<SlotTemplate, 'id'>;

function replaceSlotIfExists(slots: SlotTemplate[], slotId: string, patch: SlotPatch): boolean {
  const idx = slots.findIndex((s) => s.id === slotId);
  if (idx === -1) return false;
  pushSnapshot();
  slots[idx] = { ...patch, id: slotId };
  notify();
  return true;
}

export function updateSlotInTemplate(templateId: string, slotId: string, patch: SlotPatch): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  replaceSlotIfExists(tpl.slots, slotId, patch);
}

export function updateSlotInSubTeam(templateId: string, subTeamId: string, slotId: string, patch: SlotPatch): void {
  const tpl = taskTemplates.get(templateId);
  if (!tpl) return;
  const st = tpl.subTeams.find((s) => s.id === subTeamId);
  if (!st) return;
  replaceSlotIfExists(st.slots, slotId, patch);
}

export function updateSlotInOneTimeTask(otId: string, slotId: string, patch: SlotPatch): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  replaceSlotIfExists(ot.slots, slotId, patch);
}

export function updateSlotInOneTimeSubTeam(otId: string, subTeamId: string, slotId: string, patch: SlotPatch): void {
  const ot = oneTimeTasks.get(otId);
  if (!ot) return;
  const st = ot.subTeams.find((s) => s.id === subTeamId);
  if (!st) return;
  replaceSlotIfExists(st.slots, slotId, patch);
}

export type GroupedSlotScope = { kind: 'tpl' | 'ot'; id: string; subTeamId?: string };

/** Resolve the live SlotTemplate[] a grouped-edit scope points at, or undefined. */
function resolveGroupedScopeArray(scope: GroupedSlotScope): SlotTemplate[] | undefined {
  const owner = scope.kind === 'tpl' ? taskTemplates.get(scope.id) : oneTimeTasks.get(scope.id);
  if (!owner) return undefined;
  if (scope.subTeamId) {
    return owner.subTeams.find((s) => s.id === scope.subTeamId)?.slots;
  }
  return owner.slots;
}

/** Read-only view of a grouped-edit scope: its slots + display names. */
export function getGroupedSlotScopeView(
  scope: GroupedSlotScope,
): { slots: SlotTemplate[]; ownerName: string; subTeamName?: string } | undefined {
  const owner = scope.kind === 'tpl' ? taskTemplates.get(scope.id) : oneTimeTasks.get(scope.id);
  if (!owner) return undefined;
  if (scope.subTeamId) {
    const st = owner.subTeams.find((s) => s.id === scope.subTeamId);
    if (!st) return undefined;
    return { slots: st.slots.map((s) => ({ ...s })), ownerName: owner.name, subTeamName: st.name };
  }
  return { slots: owner.slots.map((s) => ({ ...s })), ownerName: owner.name };
}

/**
 * Apply a grouped edit to every slot in one scope (a task/one-time-task's
 * top-level slots, or one sub-team's slots) as a single undo-able action.
 *
 * Validates first (dry-run): if any slot would end with no levels or a
 * required/forbidden cert overlap, NOTHING is mutated and the offending slots
 * are returned. If the patch is a no-op (`changed === 0`) no snapshot is pushed
 * and no listeners are notified (avoids a spurious undo entry / dirty toggle).
 * Otherwise a single `pushSnapshot()` + single `notify()` wrap the whole batch.
 */
export function applyGroupedSlotEdit(
  scope: GroupedSlotScope,
  patch: GroupedSlotEdit,
): { ok: true; changed: number } | { ok: false; offending: GroupedConflict[] } {
  const arr = resolveGroupedScopeArray(scope);
  if (!arr || arr.length === 0) return { ok: true, changed: 0 };

  const v = validateGroupedApply(arr, patch);
  if (!v.ok) return { ok: false, offending: v.offending };
  if (v.changed === 0) return { ok: true, changed: 0 };

  pushSnapshot();
  _suppressSnapshot = true;
  try {
    for (let i = 0; i < arr.length; i++) {
      arr[i] = applyGroupedPatchToSlot(arr[i], patch);
    }
  } finally {
    _suppressSnapshot = false;
  }
  notify();
  return { ok: true, changed: v.changed };
}

// ─── Seed Default Data ───────────────────────────────────────────────────────

const defaultNames: string[] = [
  'איתי לוין',
  'נועה אברהמי',
  'יונתן רפאלי',
  'מאיה ישראלי',
  'עידו כהן',
  'עדי מזרחי',
  'רועי שפירא',
  'מיכל אשכנזי',
  'עומר דרוקר',
  'ענבר חזן',
  'אורי גבאי',
  'טל בן-דור',
  'דניאל וייס',
  'שירה אדרי',
  'נדב הראל',
  'ליאור פלד',
  'אסף גרינברג',
  'רוני סגל',
  'גיא מור',
  'יעל שלום',
  'אלון ברק',
  'הילה חדד',
  'מתן אלוני',
  'שחר עמר',
  'איתן דהן',
  'עמית מלכה',
  'דורון פרידמן',
  'נטע לביא',
  'יובל קליין',
  'קרן אורן',
  'אריאל נחום',
  'דנה צור',
  'אביב סוויסה',
  'גלית שדה',
  'תומר גולן',
  'ספיר מלמד',
  'אופיר ביטון',
  'נועם פרץ',
  'בועז נאמן',
  'ליהי כץ',
  'אייל רוזנפלד',
  'תמר יוספי',
  'יואב פולק',
  'סיון ריבלין',
  'אוהד שטרן',
  'רותם גנות',
  'ברק אוריון',
  'נעמה שקד',
];

const DEFAULT_L0_PAKAL_ASSIGNMENTS_BY_GROUP: Record<string, string[]> = {
  //                    L0idx: 0             1              2             3                4                5               6              7
  // Horesh cert at L0 indices 4,5 (template 8,9) — must align with HORESH_PAKAL_ID positions
  'קבוצה 1': [
    'pakal-til-lao',
    'pakal-matol',
    'pakal-negev',
    'pakal-kala',
    HORESH_PAKAL_ID,
    HORESH_PAKAL_ID,
    'pakal-rahpan',
    'pakal-matol',
  ],
  // Horesh cert at L0 index 4 (template 8)
  'קבוצה 2': [
    'pakal-rahpan',
    'pakal-matol',
    'pakal-negev',
    'pakal-kala',
    HORESH_PAKAL_ID,
    'pakal-mag',
    'pakal-matol',
    'pakal-kala',
  ],
  // Horesh cert at L0 index 4 (template 8)
  'קבוצה 3': [
    'pakal-til-lao',
    'pakal-matol',
    'pakal-negev',
    'pakal-kala',
    HORESH_PAKAL_ID,
    'pakal-matol',
    'pakal-negev',
    'pakal-mag',
  ],
  // No Horesh cert
  'קבוצה 4': [
    'pakal-mag',
    'pakal-matol',
    'pakal-negev',
    'pakal-kala',
    'pakal-rahpan',
    'pakal-til-lao',
    'pakal-matol',
    'pakal-kala',
  ],
};

function isDefaultParticipantRoster(entries: Array<{ name: string }>): boolean {
  return entries.length === defaultNames.length && entries.every((entry, index) => entry.name === defaultNames[index]);
}

function hasDesiredDefaultL0PakalSeed(
  entries: Array<{ name: string; level: Level; group: string; pakalIds?: string[] }>,
): boolean {
  if (!isDefaultParticipantRoster(entries)) return false;

  const groupIndices = new Map<string, number>();
  for (const entry of entries) {
    if (entry.level !== Level.L0) {
      if ((entry.pakalIds?.length ?? 0) > 0) return false;
      continue;
    }

    const nextIndex = groupIndices.get(entry.group) ?? 0;
    const expectedPakalId = DEFAULT_L0_PAKAL_ASSIGNMENTS_BY_GROUP[entry.group]?.[nextIndex];
    const actualPakalIds = entry.pakalIds || [];
    if (!expectedPakalId || actualPakalIds.length !== 1 || actualPakalIds[0] !== expectedPakalId) return false;
    groupIndices.set(entry.group, nextIndex + 1);
  }

  return true;
}

function needsDefaultL0PakalSeed(
  entries: Array<{ name: string; level: Level; group: string; pakalIds?: string[] }>,
): boolean {
  return isDefaultParticipantRoster(entries) && !hasDesiredDefaultL0PakalSeed(entries);
}

function applyDefaultL0PakalSeed<T extends { name: string; level: Level; group: string; pakalIds?: string[] }>(
  entries: T[],
): T[] {
  const groupIndices = new Map<string, number>();

  return entries.map((entry) => {
    if (entry.level !== Level.L0) return entry;
    const nextIndex = groupIndices.get(entry.group) ?? 0;
    const pakalId = DEFAULT_L0_PAKAL_ASSIGNMENTS_BY_GROUP[entry.group]?.[nextIndex];
    groupIndices.set(entry.group, nextIndex + 1);
    return {
      ...entry,
      pakalIds: pakalId ? [pakalId] : [],
    };
  });
}

export function seedDefaultParticipants(): void {
  // 4 Departments × 12 participants = 48 total
  // Per department:
  //   1× L4 (Nitzan)
  //   1× L3 (Nitzan)
  //   2× L2 (Nitzan)
  //   2× L0 (Nitzan)
  //   2× L0 + Hamama (Nitzan)
  //   4× L0 standard (Nitzan)
  // All have Nitzan. 2 Hamama-certified L0.
  //
  // Horesh certification defaults:
  //   Dept A: 2 standard L0 participants (indices 8,9)
  //   Dept B: 1 standard L0 participant  (index 8)
  //   Dept C: 1 standard L0 participant  (index 8)
  //   Dept D: none

  const deptNames = ['קבוצה 1', 'קבוצה 2', 'קבוצה 3', 'קבוצה 4'];

  interface MemberSpec {
    level: Level;
    certs: string[];
    tag: string;
  }
  const template: MemberSpec[] = [
    { level: Level.L4, certs: ['Nitzan', 'Hamama'], tag: 'L4' },
    { level: Level.L3, certs: ['Nitzan', 'Hamama'], tag: 'L3' },
    { level: Level.L2, certs: ['Nitzan', 'Hamama'], tag: 'L2' },
    { level: Level.L2, certs: ['Nitzan', 'Hamama'], tag: 'L2' },
    { level: Level.L0, certs: ['Nitzan'], tag: 'L0' },
    { level: Level.L0, certs: ['Nitzan'], tag: 'L0' },
    { level: Level.L0, certs: ['Nitzan', 'Hamama'], tag: 'L0-Hamama' },
    { level: Level.L0, certs: ['Nitzan', 'Hamama'], tag: 'L0-Hamama' },
    { level: Level.L0, certs: ['Nitzan'], tag: 'L0' },
    { level: Level.L0, certs: ['Nitzan'], tag: 'L0' },
    { level: Level.L0, certs: ['Nitzan'], tag: 'L0' },
    { level: Level.L0, certs: ['Nitzan'], tag: 'L0' },
  ];

  // Horesh certification per department: set of template indices
  const horeshByDept: Record<string, Set<number>> = {
    'קבוצה 1': new Set([8, 9]), // 2 standard L0 participants
    'קבוצה 2': new Set([8]), // 1 standard L0 participant
    'קבוצה 3': new Set([8]), // 1 standard L0 participant
  };

  // Per-name attribute overrides applied on top of the base seed so the
  // out-of-box demo exercises SC-9 (not-with), SC-10 (task preference),
  // HC-3 (date unavailability), and SC-3/SC-8 fair-share tuning
  // (workloadMultiplier) without manual data entry.
  type SeedDateRule = Omit<DateUnavailability, 'id'>;
  interface AttributePatch {
    preferredTaskName?: string;
    lessPreferredTaskName?: string;
    dateUnavailability?: SeedDateRule[];
    workloadMultiplier?: number;
  }
  const attributePatches: Record<string, AttributePatch> = {
    // ── קבוצה 1 ──
    'איתי לוין': { preferredTaskName: 'אדנית' },
    'נועה אברהמי': { lessPreferredTaskName: 'כרוב' },
    'עידו כהן': { preferredTaskName: 'כרוב' },
    'מאיה ישראלי': {
      lessPreferredTaskName: 'אדנית',
      dateUnavailability: [{ dayIndex: 5, startHour: 14, endHour: 17, allDay: false, reason: 'לימודים' }],
    },
    'יונתן רפאלי': { preferredTaskName: 'אדנית', lessPreferredTaskName: 'כרוב' },
    'עדי מזרחי': {
      dateUnavailability: [{ dayIndex: 2, startHour: 10, endHour: 13, allDay: false, reason: 'רופא' }],
    },
    'רועי שפירא': { preferredTaskName: 'שמש' },
    'מיכל אשכנזי': { lessPreferredTaskName: 'ממטרה' },
    'עומר דרוקר': {
      dateUnavailability: [{ dayIndex: 1, startHour: 8, endHour: 16, allDay: false, reason: 'חופש משפחתי' }],
    },
    'ענבר חזן': { preferredTaskName: 'כרוב' },
    'אורי גבאי': { lessPreferredTaskName: 'ערוגת בוקר' },
    'טל בן-דור': { preferredTaskName: 'כרוב', lessPreferredTaskName: 'ערוגת ערב' },

    // ── קבוצה 2 ──
    'דניאל וייס': { preferredTaskName: 'כרוב' },
    'שירה אדרי': { lessPreferredTaskName: 'אדנית' },
    'אסף גרינברג': { preferredTaskName: 'אדנית' },
    'ליאור פלד': {
      lessPreferredTaskName: 'כרוב',
      dateUnavailability: [{ dayIndex: 4, startHour: 16, endHour: 19, allDay: false, reason: 'אימון' }],
    },
    'נדב הראל': { preferredTaskName: 'אדנית', lessPreferredTaskName: 'כרוב' },
    'רוני סגל': {
      dateUnavailability: [{ dayIndex: 3, startHour: 9, endHour: 12, allDay: false, reason: 'תור רפואי' }],
    },
    'גיא מור': { preferredTaskName: 'חממה' },
    'יעל שלום': { lessPreferredTaskName: 'ממטרה' },
    'אלון ברק': {
      preferredTaskName: 'כרוב',
      dateUnavailability: [{ dayIndex: 5, startHour: 8, endHour: 16, allDay: false, reason: 'חופש' }],
    },
    'מתן אלוני': { lessPreferredTaskName: 'ערוגת בוקר' },
    'שחר עמר': { preferredTaskName: 'כרוב', lessPreferredTaskName: 'ערוגת ערב' },

    // ── קבוצה 3 ──
    'איתן דהן': { preferredTaskName: 'אדנית' },
    'עמית מלכה': { lessPreferredTaskName: 'כרוב' },
    'יובל קליין': { preferredTaskName: 'כרוב' },
    'נטע לביא': {
      lessPreferredTaskName: 'אדנית',
      dateUnavailability: [{ dayIndex: 6, startHour: 13, endHour: 16, allDay: false, reason: 'שבת' }],
    },
    'דורון פרידמן': { preferredTaskName: 'אדנית', lessPreferredTaskName: 'כרוב' },
    'קרן אורן': {
      dateUnavailability: [{ dayIndex: 2, startHour: 8, endHour: 11, allDay: false, reason: 'לימודים' }],
    },
    'אריאל נחום': {
      preferredTaskName: 'שמש',
      dateUnavailability: [{ dayIndex: 3, startHour: 8, endHour: 16, allDay: false, reason: 'משפחתי' }],
    },
    'דנה צור': { lessPreferredTaskName: 'ממטרה' },
    'אביב סוויסה': { preferredTaskName: 'כרוב', workloadMultiplier: 1.1 },
    'גלית שדה': { preferredTaskName: 'כרוב', lessPreferredTaskName: 'ערוגת ערב' },
    'ספיר מלמד': { lessPreferredTaskName: 'ערוגת בוקר' },

    // ── קבוצה 4 ──
    'אופיר ביטון': { preferredTaskName: 'כרוב' },
    'נועם פרץ': { lessPreferredTaskName: 'אדנית' },
    'אייל רוזנפלד': { preferredTaskName: 'אדנית' },
    'ליהי כץ': { lessPreferredTaskName: 'כרוב' },
    'בועז נאמן': { preferredTaskName: 'אדנית', lessPreferredTaskName: 'כרוב' },
    'תמר יוספי': {
      dateUnavailability: [{ dayIndex: 4, startHour: 11, endHour: 14, allDay: false, reason: 'תור' }],
    },
    'יואב פולק': {
      preferredTaskName: 'חממה',
      dateUnavailability: [{ dayIndex: 7, startHour: 8, endHour: 16, allDay: false, reason: 'חופש' }],
    },
    'סיון ריבלין': {
      lessPreferredTaskName: 'ממטרה',
      dateUnavailability: [{ dayIndex: 1, startHour: 15, endHour: 18, allDay: false, reason: 'לימודים' }],
    },
    'אוהד שטרן': { preferredTaskName: 'כרוב', workloadMultiplier: 0.9 },
    'רותם גנות': { lessPreferredTaskName: 'ערוגת בוקר' },
    'נעמה שקד': { preferredTaskName: 'כרוב', lessPreferredTaskName: 'ערוגת ערב' },
  };

  // Bidirectional not-with pairs. Each edge listed once — addNotWith mirrors it.
  // 12 within-group + 4 cross-group; "choosers" are 3 L0s per group
  // (2 pick one partner, 1 picks two).
  const notWithPairs: Array<[string, string]> = [
    ['עדי מזרחי', 'טל בן-דור'],
    ['רועי שפירא', 'יעל שלום'], // cross G1↔G2
    ['עומר דרוקר', 'ענבר חזן'],
    ['עומר דרוקר', 'אורי גבאי'],
    ['רוני סגל', 'שחר עמר'],
    ['גיא מור', 'הילה חדד'],
    ['אלון ברק', 'מתן אלוני'],
    ['אלון ברק', 'דנה צור'], // cross G2↔G3
    ['קרן אורן', 'תומר גולן'],
    ['אריאל נחום', 'גלית שדה'],
    ['אביב סוויסה', 'ספיר מלמד'],
    ['אביב סוויסה', 'אורי גבאי'], // cross G3↔G1
    ['תמר יוספי', 'ברק אוריון'],
    ['יואב פולק', 'רותם גנות'],
    ['אוהד שטרן', 'נעמה שקד'],
    ['אוהד שטרן', 'רוני סגל'], // cross G4↔G2
  ];

  let nameIdx = 0;
  const l0PakalIndexByDept = new Map<string, number>();
  const nameToId = new Map<string, string>();
  for (const dept of deptNames) {
    const horeshIndices = horeshByDept[dept];
    template.forEach((spec, i) => {
      const id = uid('p');
      const certs = [...spec.certs];
      if (horeshIndices?.has(i)) certs.push('Horesh');
      const nextL0Index = l0PakalIndexByDept.get(dept) ?? 0;
      const pakalId = spec.level === Level.L0 ? DEFAULT_L0_PAKAL_ASSIGNMENTS_BY_GROUP[dept]?.[nextL0Index] : undefined;
      if (spec.level === Level.L0) l0PakalIndexByDept.set(dept, nextL0Index + 1);
      const name = defaultNames[nameIdx++];
      const patch = attributePatches[name];
      const p: Participant = {
        id,
        name,
        level: spec.level,
        certifications: certs,
        pakalIds: pakalId ? [pakalId] : [],
        group: dept,
        availability: getDefaultAvailability(),
        dateUnavailability: [],
      };
      if (patch?.preferredTaskName) p.preferredTaskName = patch.preferredTaskName;
      if (patch?.lessPreferredTaskName) p.lessPreferredTaskName = patch.lessPreferredTaskName;
      if (patch?.workloadMultiplier !== undefined && patch.workloadMultiplier !== 1) {
        p.workloadMultiplier = patch.workloadMultiplier;
      }
      participants.set(id, p);
      nameToId.set(name, id);
    });
  }

  for (const [name, patch] of Object.entries(attributePatches)) {
    if (!patch.dateUnavailability) continue;
    const pid = nameToId.get(name);
    if (!pid) continue;
    for (const rule of patch.dateUnavailability) {
      addDateUnavailability(pid, rule);
    }
  }

  for (const [nameA, nameB] of notWithPairs) {
    const idA = nameToId.get(nameA);
    const idB = nameToId.get(nameB);
    if (idA && idB) addNotWith(idA, idB);
  }

  notify();
}

export function seedDefaultTaskTemplates(): void {
  // Seed default rest rule (HC-14) — 5-hour minimum gap
  const defaultRestRule: RestRule = { id: uid('rr'), label: 'הפסקה מינימלית', durationHours: 5 };
  _restRules.push(defaultRestRule);

  // Adanit
  const adanitTpl = addTaskTemplate({
    name: 'אדנית',

    durationHours: 8,
    shiftsPerDay: 3,
    startHour: 5,
    sameGroupRequired: true,
    baseLoadWeight: 1,
    loadWindows: [],
    blocksConsecutive: true,
    togethernessRelevant: true,
    subTeams: [
      {
        id: uid('st'),
        name: 'סגול ראשי',
        slots: [
          {
            id: uid('slot'),
            label: 'משתתף בסגול א',
            acceptableLevels: [{ level: Level.L0 }],
            requiredCertifications: ['Nitzan'],
          },
          {
            id: uid('slot'),
            label: 'משתתף בסגול א',
            acceptableLevels: [{ level: Level.L0 }],
            requiredCertifications: ['Nitzan'],
          },
          {
            id: uid('slot'),
            label: 'סגל בסגול א',
            acceptableLevels: [{ level: Level.L3 }, { level: Level.L4 }],
            requiredCertifications: ['Nitzan'],
          },
        ],
      },
      {
        id: uid('st'),
        name: 'סגול משני',
        slots: [
          {
            id: uid('slot'),
            label: 'משתתף בסגול ב',
            acceptableLevels: [{ level: Level.L0 }],
            requiredCertifications: ['Nitzan'],
          },
          {
            id: uid('slot'),
            label: 'משתתף בסגול ב',
            acceptableLevels: [{ level: Level.L0 }],
            requiredCertifications: ['Nitzan'],
          },
          {
            id: uid('slot'),
            label: "בכיר בסגול ב'",
            acceptableLevels: [{ level: Level.L2 }],
            requiredCertifications: ['Nitzan'],
          },
        ],
      },
    ],
    slots: [],
    restRuleId: defaultRestRule.id,
    sleepRecovery: { triggerShifts: [3], recoveryHours: 7 },
    color: '#4A90D9',
    displayOrder: 0,
  });

  // Hamama — loadFormula is attached at the end of this function,
  // once ערוגת ערב has been seeded (Hamama's formula references it).
  const hamamaTpl = addTaskTemplate({
    name: 'חממה',

    durationHours: 12,
    shiftsPerDay: 2,
    startHour: 6,
    sameGroupRequired: false,
    baseLoadWeight: 5 / 6,
    loadWindows: [],
    blocksConsecutive: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'חממה מפעיל',
        acceptableLevels: [{ level: Level.L0 }, { level: Level.L4, lowPriority: true }],
        requiredCertifications: ['Hamama'],
      },
    ],
    // HC-15: night-shift only (shift 2 ends 06:00 → no loaded task until 13:00).
    sleepRecovery: { triggerShifts: [2], recoveryHours: 7 },
    splittable: true,
    color: '#E74C3C',
    displayOrder: 1,
  });

  // Shemesh
  const shemeshTpl = addTaskTemplate({
    name: 'שמש',

    durationHours: 4,
    shiftsPerDay: 6,
    startHour: 5,
    sameGroupRequired: false,
    baseLoadWeight: 1,
    loadWindows: [],
    blocksConsecutive: true,
    togethernessRelevant: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'משתתף בשמש',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בשמש',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    restRuleId: defaultRestRule.id,
    sleepRecovery: { triggerShifts: [6], recoveryHours: 7 },
    splittable: true,
    color: '#F39C12',
    displayOrder: 4,
  });

  // Mamtera — default load derived via the calculator:
  // 14 hours of Mamtera = 8 hours of Adanit (base) + 2.5 hours of Shemesh (base) → 0.75
  const mamteraLoadFormula = buildFormula(
    [
      { refTemplateId: adanitTpl.id, refRate: { kind: 'base' }, hours: 8 },
      { refTemplateId: shemeshTpl.id, refRate: { kind: 'base' }, hours: 2.5 },
    ],
    taskTemplates,
    14,
  );
  addTaskTemplate({
    name: 'ממטרה',

    durationHours: 14,
    shiftsPerDay: 1,
    startHour: 9,
    sameGroupRequired: false,
    baseLoadWeight: mamteraLoadFormula.computedValue,
    loadFormula: mamteraLoadFormula,
    loadWindows: [],
    blocksConsecutive: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'משתתף בממטרה',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: [],
        forbiddenCertifications: ['Horesh'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בממטרה',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: [],
        forbiddenCertifications: ['Horesh'],
      },
    ],
    splittable: true,
    color: '#27AE60',
    displayOrder: 3,
  });

  // Karov — base load derived via the calculator:
  // 24 hours of Karov (base) = 8 hours of Adanit (base) → 8/24 ≈ 0.33
  // (Hot-window weights are independent, defined below.)
  const karovBaseLoadFormula = buildFormula(
    [{ refTemplateId: adanitTpl.id, refRate: { kind: 'base' }, hours: 8 }],
    taskTemplates,
    24,
  );
  addTaskTemplate({
    name: 'כרוב',

    durationHours: 8,
    shiftsPerDay: 3,
    startHour: 5,
    sameGroupRequired: true,
    baseLoadWeight: karovBaseLoadFormula.computedValue,
    loadFormula: karovBaseLoadFormula,
    blocksConsecutive: false,
    loadWindows: [
      {
        id: uid('lw'),
        startHour: 5,
        startMinute: 0,
        endHour: 6,
        endMinute: 30,
        weight: 1,
      },
      {
        id: uid('lw'),
        startHour: 17,
        startMinute: 0,
        endHour: 18,
        endMinute: 30,
        weight: 1,
      },
    ],
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'מפקד כרוב (דרגה 2/3/4)',
        acceptableLevels: [{ level: Level.L2 }, { level: Level.L3 }, { level: Level.L4 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בכרוב',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בכרוב',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    color: '#8E44AD',
    displayOrder: 0,
  });

  // Karovit
  addTaskTemplate({
    name: 'כרובית',

    durationHours: 8,
    shiftsPerDay: 3,
    startHour: 5,
    sameGroupRequired: true,
    baseLoadWeight: 0,
    loadWindows: [],
    blocksConsecutive: false,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'סגל כרובית',
        acceptableLevels: [{ level: Level.L2 }, { level: Level.L3 }, { level: Level.L4 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בכרובית',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בכרובית',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בכרובית',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    color: '#BDC3C7',
    displayOrder: 0,
  });

  // ערוגת בוקר
  addTaskTemplate({
    name: 'ערוגת בוקר',

    durationHours: 1.5,
    shiftsPerDay: 1,
    startHour: 5,
    sameGroupRequired: false,
    baseLoadWeight: 1,
    loadWindows: [],
    blocksConsecutive: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'משתתף בערוגת בוקר',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בערוגת בוקר',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    color: '#1ABC9C',
    displayOrder: 2,
  });

  // ערוגת ערב
  const arugatErevTpl = addTaskTemplate({
    name: 'ערוגת ערב',

    durationHours: 1.5,
    shiftsPerDay: 1,
    startHour: 17,
    sameGroupRequired: false,
    baseLoadWeight: 1,
    loadWindows: [],
    blocksConsecutive: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'משתתף בערוגת ערב',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף בערוגת ערב',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    color: '#1ABC9C',
    displayOrder: 2,
  });

  // Hamama default load derived via the calculator:
  // 12 hours of Hamama = 8 hours of Adanit (base) + 1.5 hours of ערוגת ערב (base) → 9.5/12 ≈ 0.79
  const hamamaLoadFormula = buildFormula(
    [
      { refTemplateId: adanitTpl.id, refRate: { kind: 'base' }, hours: 8 },
      { refTemplateId: arugatErevTpl.id, refRate: { kind: 'base' }, hours: 1.5 },
    ],
    taskTemplates,
    12,
  );
  updateTaskTemplate(hamamaTpl.id, { loadFormula: hamamaLoadFormula });
}

/**
 * Seed a single OneTimeTask so the out-of-box demo exercises the
 * post-generation injection feature surface (one-time UI flow + engine
 * scheduling of a OneTimeTask alongside templates).
 *
 * Depends on the default rest rule already created by
 * `seedDefaultTaskTemplates()` — must run after it.
 */
export function seedDefaultOneTimeTasks(): void {
  const defaultRule = _restRules.find((r) => !r.deleted);
  if (!defaultRule) return; // template seed didn't run; nothing to anchor against

  const d = scheduleDate;
  // Day 3 of the schedule — calendar date two days after scheduleDate.
  const day3 = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 2);

  addOneTimeTask({
    name: 'יזומה',
    scheduledDate: day3,
    startHour: 21,
    startMinute: 0,
    durationHours: 4,
    sameGroupRequired: true,
    blocksConsecutive: true,
    baseLoadWeight: 1,
    loadWindows: [],
    togethernessRelevant: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'מפקד יזומה',
        acceptableLevels: [{ level: Level.L2 }, { level: Level.L3 }, { level: Level.L4 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף ביזומה',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף ביזומה',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
      {
        id: uid('slot'),
        label: 'משתתף ביזומה',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    restRuleId: defaultRule.id,
    // HC-15: OneTimeTask shiftIndex is always 1, so triggerShifts must be [1].
    sleepRecovery: { triggerShifts: [1], recoveryHours: 7 },
    color: '#8E44AD',
    displayOrder: 99,
    description: 'משימה חד-פעמית — משמרת לילה יזומה',
  });
}

/**
 * Build the "נהר אביב" built-in task set without touching the live store.
 * Hamama and Mamtera mirror the default seed (מכרסם); Adanit has a single
 * segol (flat slots, no sub-teams) and Shemesh has 3 slots. No one-time tasks.
 */
function _buildNaharAvivTaskSetState(): Pick<TaskSet, 'templates' | 'oneTimeTasks' | 'restRules'> {
  const restRule: RestRule = { id: uid('rr'), label: 'הפסקה מינימלית', durationHours: 5 };
  const templates = new Map<string, TaskTemplate>();
  const add = (spec: Omit<TaskTemplate, 'id'>): TaskTemplate => {
    const tpl = _materializeTemplate(spec);
    templates.set(tpl.id, tpl);
    return tpl;
  };

  const adanitTpl = add({
    name: 'אדנית',
    durationHours: 8,
    shiftsPerDay: 3,
    startHour: 5,
    sameGroupRequired: true,
    baseLoadWeight: 1,
    loadWindows: [],
    blocksConsecutive: true,
    togethernessRelevant: true,
    subTeams: [],
    slots: [
      ...[1, 2, 3].map(() => ({
        id: uid('slot'),
        label: 'משתתף באדנית',
        acceptableLevels: [{ level: Level.L0 }],
        requiredCertifications: ['Nitzan'],
      })),
      {
        id: uid('slot'),
        label: 'סגל באדנית',
        acceptableLevels: [{ level: Level.L2 }, { level: Level.L3 }, { level: Level.L4 }],
        requiredCertifications: ['Nitzan'],
      },
    ],
    restRuleId: restRule.id,
    sleepRecovery: { triggerShifts: [3], recoveryHours: 7 },
    color: '#4A90D9',
    displayOrder: 0,
  });

  // Same weight as the default seed's Hamama (8h Adanit + 1.5h ערוגת ערב per
  // 12h). Kept as a plain value: ערוגת ערב is not part of this set, so a
  // formula referencing it would be permanently stale.
  add({
    name: 'חממה',
    durationHours: 12,
    shiftsPerDay: 2,
    startHour: 6,
    sameGroupRequired: false,
    baseLoadWeight: 9.5 / 12,
    loadWindows: [],
    blocksConsecutive: true,
    subTeams: [],
    slots: [
      {
        id: uid('slot'),
        label: 'חממה מפעיל',
        acceptableLevels: [{ level: Level.L0 }, { level: Level.L4, lowPriority: true }],
        requiredCertifications: ['Hamama'],
      },
    ],
    sleepRecovery: { triggerShifts: [2], recoveryHours: 7 },
    splittable: true,
    color: '#E74C3C',
    displayOrder: 1,
  });

  const shemeshTpl = add({
    name: 'שמש',
    durationHours: 4,
    shiftsPerDay: 6,
    startHour: 5,
    sameGroupRequired: false,
    baseLoadWeight: 1,
    loadWindows: [],
    blocksConsecutive: true,
    togethernessRelevant: true,
    subTeams: [],
    slots: [1, 2, 3].map(() => ({
      id: uid('slot'),
      label: 'משתתף בשמש',
      acceptableLevels: [{ level: Level.L0 }],
      requiredCertifications: ['Nitzan'],
    })),
    restRuleId: restRule.id,
    sleepRecovery: { triggerShifts: [6], recoveryHours: 7 },
    splittable: true,
    color: '#F39C12',
    displayOrder: 4,
  });

  // 14 hours of Mamtera = 8 hours of Adanit (base) + 2.5 hours of Shemesh (base) → 0.75
  const mamteraLoadFormula = buildFormula(
    [
      { refTemplateId: adanitTpl.id, refRate: { kind: 'base' }, hours: 8 },
      { refTemplateId: shemeshTpl.id, refRate: { kind: 'base' }, hours: 2.5 },
    ],
    templates,
    14,
  );
  add({
    name: 'ממטרה',
    durationHours: 14,
    shiftsPerDay: 1,
    startHour: 9,
    sameGroupRequired: false,
    baseLoadWeight: mamteraLoadFormula.computedValue,
    loadFormula: mamteraLoadFormula,
    loadWindows: [],
    blocksConsecutive: true,
    subTeams: [],
    slots: [1, 2].map(() => ({
      id: uid('slot'),
      label: 'משתתף בממטרה',
      acceptableLevels: [{ level: Level.L0 }],
      requiredCertifications: [],
      forbiddenCertifications: ['Horesh'],
    })),
    splittable: true,
    color: '#27AE60',
    displayOrder: 3,
  });

  return {
    templates: JSON.parse(JSON.stringify([...templates.values()])) as TaskTemplate[],
    oneTimeTasks: [],
    restRules: [restRule],
  };
}

// ─── Initialization ──────────────────────────────────────────────────────────

let _initRunning = false;

export function initStore(): void {
  if (_initRunning) {
    console.warn('[Store] initStore() re-entrance blocked');
    return;
  }
  _initRunning = true;
  try {
    participants.clear();
    dateUnavailabilities.clear();
    taskTemplates.clear();
    oneTimeTasks.clear();
    pakalDefinitions = clonePakalDefinitions(DEFAULT_PAKAL_DEFINITIONS);
    undoStack.length = 0;
    redoStack.length = 0;
    // Reset lazy-loaded participant sets so they re-initialise
    _participantSets = null;
    _activeParticipantSetId = undefined;
    // Reset lazy-loaded task sets so they re-initialise
    _taskSets = null;
    _activeTaskSetId = undefined;
    // Try to load from storage first
    if (loadFromStorage()) {
      console.log('[Store] Restored state from localStorage');
      // Ensure participant sets are initialised (first-load seeding)
      _initParticipantSets();
      _initTaskSets();
      return;
    }
    // Suppress snapshots during seed (initial state shouldn't be undoable)
    _suppressSnapshot = true;
    seedDefaultParticipants();
    seedDefaultTaskTemplates();
    seedDefaultOneTimeTasks();
    // Seed built-in participant set from demo data
    _initParticipantSets();
    _initTaskSets();
  } finally {
    _suppressSnapshot = false;
    _initRunning = false;
  }
}

// ─── Live Mode State ─────────────────────────────────────────────────────────

let liveModeState: LiveModeState = {
  enabled: false,
  currentTimestamp: new Date(),
};

export function getLiveModeState(): LiveModeState {
  return { ...liveModeState, currentTimestamp: new Date(liveModeState.currentTimestamp.getTime()) };
}

export function setLiveModeEnabled(enabled: boolean): void {
  liveModeState.enabled = enabled;
  // Always refresh timestamp to "now" so the day picker reflects the current day
  liveModeState.currentTimestamp = new Date();
  // Persist to the dedicated tiny key rather than rewriting the full state
  // blob. The live-mode checkbox handler in app.ts already calls renderAll().
  _saveLiveMode();
}

export function setLiveModeTimestamp(timestamp: Date): void {
  liveModeState.currentTimestamp = timestamp;
  // Persist to the dedicated tiny key rather than rewriting the full state
  // blob. The caller already triggers renderAll() when needed.
  _saveLiveMode();
}

/**
 * Persist live-mode state to its own small localStorage key.
 * Kept separate from `saveToStorage()` so that live-mode ticks (which can
 * happen every few seconds) don't rewrite the much larger full-state blob.
 */
function _saveLiveMode(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY_LIVE_MODE,
      JSON.stringify({
        enabled: liveModeState.enabled,
        currentTimestamp: liveModeState.currentTimestamp.toISOString(),
      }),
    );
    onSaveSuccess();
  } catch (err) {
    console.warn('[Store] Failed to save live mode:', err);
    reportSaveError(err);
  }
}

function _loadLiveMode(): LiveModeState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LIVE_MODE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { enabled?: unknown; currentTimestamp?: unknown };
    const ts = typeof parsed.currentTimestamp === 'string' ? new Date(parsed.currentTimestamp) : new Date();
    return {
      enabled: !!parsed.enabled,
      currentTimestamp: Number.isNaN(ts.getTime()) ? new Date() : ts,
    };
  } catch (err) {
    console.warn('[Store] Failed to load live mode:', err);
    return null;
  }
}

// ─── localStorage Persistence ────────────────────────────────────────────────

const STORAGE_KEY_STATE = 'gardenmanager_state';
const STORAGE_KEY_SCHEDULE = 'gardenmanager_schedule';
const STORAGE_KEY_LIVE_MODE = 'gardenmanager_live_mode';

let _saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DEBOUNCE_MS = 500;

// Date-preserving JSON (de)serialization lives in a pure, DOM-free shared
// module (imported at the top of this file) so the web build and the
// pure-`src/` Node coverage tests share ONE implementation — no hand-maintained
// mirror that can silently drift. This re-export keeps the historical
// `store.jsonSerialize` / `store.jsonDeserialize` API stable for external
// consumers; in-module call sites (loadSchedule, snapshot deep-copy, etc.) use
// the imported bindings directly and are unchanged.
export { jsonDeserialize, jsonSerialize };

/**
 * Save the full store state to localStorage.
 * Called automatically (debounced) after every store mutation.
 */
export function saveToStorage(): void {
  // When the quota is exhausted, _storageWedged is true. We still attempt the
  // write so that onSaveSuccess() can clear the wedge once space is freed.
  // Toast flooding is prevented by the cooldown in reportSaveError().
  try {
    const state = {
      version: 7,
      scheduleDate: scheduleDate.toISOString(),
      scheduleDays,
      restRules: _restRules,
      // Live mode is persisted separately via STORAGE_KEY_LIVE_MODE; keeping
      // it out of the big blob means clock ticks don't rewrite everything.
      pakalDefinitions,
      certificationDefinitions,
      // Omit inline dateUnavailability from participant serialization —
      // the dateUnavailabilities Map is the single source of truth
      // (serialized separately below).
      participants: Array.from(participants.values()).map((p) => {
        const { dateUnavailability: _, ...rest } = p;
        return {
          ...rest,
          availability: p.availability.map((w) => ({
            start: w.start.toISOString(),
            end: w.end.toISOString(),
          })),
        };
      }),
      dateUnavailabilities: Array.from(dateUnavailabilities.entries()).map(([pid, rules]) => ({
        pid,
        rules: rules.map(({ id: _, ...rest }) => rest),
      })),
      taskTemplates: Array.from(taskTemplates.values()),
      oneTimeTasks: Array.from(oneTimeTasks.values()).map((ot) => ({
        ...ot,
        scheduledDate: ot.scheduledDate.toISOString(),
      })),
      notWithPairs: Array.from(notWithPairs.entries()).map(([pid, set]) => ({
        pid,
        targets: [...set],
      })),
      // Stays in lock-step with the master data above (same write) — see
      // `_masterDataRevision`. Older blobs lack it and load as 0.
      masterDataRevision: _masterDataRevision,
    };
    localStorage.setItem(STORAGE_KEY_STATE, JSON.stringify(state));
    onSaveSuccess();
  } catch (err) {
    console.warn('[Store] Failed to save to localStorage:', err);
    reportSaveError(err);
  }
}

/**
 * Load the full store state from localStorage.
 * Returns true if state was successfully restored.
 */
export function loadFromStorage(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_STATE);
    if (!raw) return false;

    const state = JSON.parse(raw);
    if (!state || state.version !== 7) return false;

    // Restore schedule date/days
    scheduleDate = new Date(state.scheduleDate);
    // Clamp on load: setScheduleDays() clamps to 1..7, but a corrupt/hand-edited
    // gardenmanager_state could carry an out-of-range value that would widen the
    // availability window and freeze onto Schedule.periodDays. (Falsy → 7.)
    const sd = state.scheduleDays;
    scheduleDays = typeof sd === 'number' && Number.isFinite(sd) ? Math.max(1, Math.min(7, Math.floor(sd))) : 7;
    _restRules = Array.isArray(state.restRules) ? state.restRules : [];
    // Restore the master-data revision counter (see `_masterDataRevision`).
    // Version-7 blobs written before this field existed lack it → default to 0.
    // loadFromStorage sets the store's maps directly (it never calls notify()),
    // and recalcAllAvailability() below only mutates in place, so nothing here
    // re-bumps the counter after this assignment.
    _masterDataRevision =
      typeof state.masterDataRevision === 'number' && Number.isFinite(state.masterDataRevision)
        ? state.masterDataRevision
        : 0;

    // Reset stale schedule dates to the next upcoming Sunday:
    // (a) Non-Sunday dates can result from continuity replanning ("generate from
    //     day X"), which shifts scheduleDate mid-week. Since the schedule output
    //     is cleared on every app restart, this mid-week date is no longer relevant.
    // (b) Schedule dates whose entire window has elapsed are also outdated.
    const schedEndForStaleness = new Date(
      scheduleDate.getFullYear(),
      scheduleDate.getMonth(),
      scheduleDate.getDate() + scheduleDays,
    );
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    if (scheduleDate.getDay() !== 0 || schedEndForStaleness < todayMidnight) {
      scheduleDate = defaultScheduleDate();
    }

    // Restore live mode state — prefer the dedicated key (written by
    // _saveLiveMode); fall back to the legacy inlined value for users whose
    // state blob was persisted before the split. If neither source has data
    // (common: user has never enabled live mode), reset to default so a
    // previous in-memory `enabled:true` (e.g. left over from the tutorial demo
    // that just exited) doesn't leak into the restored session.
    const dedicatedLive = _loadLiveMode();
    if (dedicatedLive) {
      liveModeState = dedicatedLive;
    } else if (state.liveMode) {
      liveModeState = {
        enabled: state.liveMode.enabled || false,
        currentTimestamp: new Date(state.liveMode.currentTimestamp),
      };
    } else {
      liveModeState = { enabled: false, currentTimestamp: new Date() };
    }

    // Restore participants
    participants.clear();
    dateUnavailabilities.clear();
    pakalDefinitions = state.pakalDefinitions?.length
      ? normalizePakalDefinitions(state.pakalDefinitions)
      : clonePakalDefinitions(DEFAULT_PAKAL_DEFINITIONS);
    certificationDefinitions = (
      state.certificationDefinitions?.length ? state.certificationDefinitions : DEFAULT_CERTIFICATION_DEFINITIONS
    ).map((d: CertificationDefinition) => ({ ...d, color: sanitizeColor(d.color) }));

    for (const pData of Array.isArray(state.participants) ? state.participants : []) {
      const p: Participant = {
        ...pData,
        // Charset-sanitize the id at the load boundary: a tampered/full-backup
        // gardenmanager_state can carry a crafted id that would break out of the
        // raw `data-pid="${p.id}"` sidebar/grid sinks. No-op for legit uid() ids.
        id: sanitizeParticipantId(pData.id),
        pakalIds: sanitizePakalIds(pData.pakalIds, pakalDefinitions),
        availability: (pData.availability || []).map((w: { start: string; end: string }) => ({
          start: new Date(w.start),
          end: new Date(w.end),
        })),
        dateUnavailability: [],
      };
      // Clamp the fair-share workload multiplier at the load boundary: a
      // tampered / hand-edited blob (incl. a full-backup-planted state) bypasses
      // the UI/XLSX clamps, and a huge value (1e308) collapses this person's
      // SC-3/SC-8 fairness target. Omit when it normalizes to the neutral
      // default, matching the add/snapshot "store only when ≠ 1" convention.
      const wm = clampWorkloadMultiplier(pData.workloadMultiplier);
      if (wm === 1) delete p.workloadMultiplier;
      else p.workloadMultiplier = wm;
      participants.set(p.id, p);
    }

    // Restore date unavailabilities
    const duList = Array.isArray(state.dateUnavailabilities) ? state.dateUnavailabilities : [];
    for (const entry of duList) {
      const rules: DateUnavailability[] = (entry.rules || [])
        .map((rule: Partial<Omit<DateUnavailability, 'id'>>) => normalizeDateUnavailabilityRule(rule))
        .filter((rule: Omit<DateUnavailability, 'id'> | null): rule is Omit<DateUnavailability, 'id'> => !!rule)
        .map((rule: Omit<DateUnavailability, 'id'>) => ({ ...rule, id: uid('du') }));
      if (rules.length > 0) {
        // Sanitize the pid key in lock-step with the participant id above so the
        // `p.dateUnavailability = dateUnavailabilities.get(id)` sync still resolves.
        dateUnavailabilities.set(sanitizeParticipantId(entry.pid), rules);
      }
    }

    // Sync participant inline dateUnavailability to the canonical Map
    // so both references stay aligned.
    for (const [id, p] of participants) {
      p.dateUnavailability = dateUnavailabilities.get(id) || [];
    }

    // Restore task templates. Numeric fields (durationHours/shiftsPerDay/
    // startHour) are clamped at this import boundary via
    // sanitizeTemplateNumericFields: the localStorage blob (incl. one planted by
    // a tampered full-backup) is untrusted and bypasses the UI clamps, so a
    // crafted Infinity (1e999) or overflow-to-Infinity (1e308) duration would
    // otherwise reach generateTasksFromTemplates and produce an Invalid-Date
    // timeBlock. Mirrors the rest-rule clamp model (sanitizeRestDurationHours).
    taskTemplates.clear();
    for (const tpl of Array.isArray(state.taskTemplates) ? state.taskTemplates : []) {
      taskTemplates.set(tpl.id, sanitizeTemplateNumericFields({ ...tpl, color: _safeColor(tpl.color) }));
    }

    // Restore one-time tasks
    // Same import-boundary numeric clamp as templates above (one-time tasks
    // carry durationHours/startHour — sanitizeTemplateNumericFields leaves the
    // absent shiftsPerDay untouched).
    oneTimeTasks.clear();
    for (const ot of Array.isArray(state.oneTimeTasks) ? state.oneTimeTasks : []) {
      oneTimeTasks.set(
        ot.id,
        sanitizeTemplateNumericFields({
          ...ot,
          color: _safeColor(ot.color),
          scheduledDate: new Date(ot.scheduledDate),
          loadWindows: (ot.loadWindows || []).map((w: any) => ({ ...w })),
          slots: (ot.slots || []).map((s: any) => ({
            ...s,
            acceptableLevels: [...s.acceptableLevels],
            requiredCertifications: [...s.requiredCertifications],
          })),
          subTeams: (ot.subTeams || []).map((st: any) => ({
            ...st,
            slots: st.slots.map((s: any) => ({
              ...s,
              acceptableLevels: [...s.acceptableLevels],
              requiredCertifications: [...s.requiredCertifications],
            })),
          })),
        }),
      );
    }

    // Restore notWithPairs
    notWithPairs.clear();
    for (const entry of Array.isArray(state.notWithPairs) ? state.notWithPairs : []) {
      if (entry.pid && Array.isArray(entry.targets) && entry.targets.length > 0) {
        // Both the pid key and every target are participant ids — sanitize them
        // with the same transform so the pairing still resolves after the id
        // above was charset-cleaned. syncNotWithToParticipants() then rebuilds
        // each p.notWithIds from this (sanitized) map.
        notWithPairs.set(
          sanitizeParticipantId(entry.pid),
          new Set((entry.targets as unknown[]).map(sanitizeParticipantId)),
        );
      }
    }
    syncNotWithToParticipants();

    // Recompute availability from canonical inputs instead of using the
    // stale windows that were serialized at save time.
    recalcAllAvailability();

    return true;
  } catch (err) {
    console.warn('[Store] Failed to load from localStorage:', err);
    return false;
  }
}

/**
 * Save a full Schedule object to localStorage.
 * Called after generation, swaps, locks, and rescue-apply operations.
 */
export function saveSchedule(schedule: Schedule): boolean {
  try {
    localStorage.setItem(STORAGE_KEY_SCHEDULE, jsonSerialize(schedule));
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save schedule to localStorage:', err);
    reportSaveError(err);
    return false;
  }
}

export function clearSchedule(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_SCHEDULE);
  } catch (err) {
    console.warn('[Store] Failed to clear schedule from localStorage:', err);
  }
}

/**
 * Load a saved Schedule from localStorage.
 * Returns null if no schedule is saved or deserialization fails.
 */
export function loadSchedule(): Schedule | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SCHEDULE);
    if (!raw) return null;
    const schedule = jsonDeserialize<Schedule>(raw);
    // A persisted schedule (incl. one planted by a tampered full-backup import,
    // which writes gardenmanager_schedule verbatim) can carry unsanitized frozen
    // colors. Re-coerce them on load, mirroring how the snapshot library load
    // (_sanitizeSnapshots) and the template/cert load boundaries do — so the
    // many raw `style="…${task.color}…"` board/tooltip/PDF/Excel sinks that read
    // the active schedule directly cannot be reached by a crafted color.
    if (schedule?.tasks) {
      for (const task of schedule.tasks) task.color = _safeColor(task.color);
    }
    if (schedule?.continuitySnapshot) sanitizeContinuitySnapshotColors(schedule.continuitySnapshot);
    // Same threat as the colors above: a tampered full-backup writes
    // gardenmanager_schedule verbatim, so a crafted participant id reaches the
    // frozen participants that feed the raw `data-pid` board/sidebar sinks.
    _sanitizeScheduleParticipantIds(schedule);
    return schedule;
  } catch (err) {
    console.warn('[Store] Failed to load schedule from localStorage:', err);
    return null;
  }
}

/**
 * Clear all persisted state (reset to defaults on next load).
 * Delegates to factoryReset() so every reset path is comprehensive.
 */
export function clearStorage(): void {
  factoryReset();
}

/**
 * Full factory reset: clear ALL persisted data and in-memory caches,
 * including UI preferences (theme, sidebar, default attempts).
 * After calling this, a page reload will re-seed default data via initStore().
 */
export function factoryReset(): void {
  if (_saveDebounceTimer) {
    clearTimeout(_saveDebounceTimer);
    _saveDebounceTimer = null;
  }
  try {
    localStorage.removeItem(STORAGE_KEY_STATE);
    localStorage.removeItem(STORAGE_KEY_SCHEDULE);
    localStorage.removeItem(STORAGE_KEY_LIVE_MODE);
    localStorage.removeItem(STORAGE_KEY_ALGORITHM);
    localStorage.removeItem(STORAGE_KEY_PRESETS);
    localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET);
    localStorage.removeItem(STORAGE_KEY_SNAPSHOTS);
    localStorage.removeItem(STORAGE_KEY_ACTIVE_SNAPSHOT);
    localStorage.removeItem(STORAGE_KEY_PSETS);
    localStorage.removeItem(STORAGE_KEY_ACTIVE_PSET);
    localStorage.removeItem(STORAGE_KEY_TASK_SETS);
    localStorage.removeItem(STORAGE_KEY_ACTIVE_TASK_SET);
    localStorage.removeItem('gardenmanager_default_attempts');
    localStorage.removeItem('gardenmanager_theme');
    localStorage.removeItem('gm-sidebar-collapsed');
    localStorage.removeItem('gardenmanager_tutorial_banner_dismissed');
    localStorage.removeItem('gardenmanager_tutorial_seen_tracks');
    localStorage.removeItem('gardenmanager_install_nudge_seen');
    // Tutorial demo writes a pre-tour backup snapshot under this key (see
    // BACKUP_KEY in tutorial-demo.ts). If left in place, the next page-load
    // runs restoreTutorialBackupIfPresent() before initStore() and silently
    // rewrites every key we just cleared — undoing the reset.
    localStorage.removeItem('gardenmanager_pre_tutorial_snapshot');
    // A factory reset frees space, so clear the wedge latch to resume saves.
    onSaveSuccess();
  } catch (err) {
    console.warn('[Store] Failed to clear localStorage:', err);
  }
  participants.clear();
  dateUnavailabilities.clear();
  notWithPairs.clear();
  taskTemplates.clear();
  oneTimeTasks.clear();
  pakalDefinitions = clonePakalDefinitions(DEFAULT_PAKAL_DEFINITIONS);
  certificationDefinitions = DEFAULT_CERTIFICATION_DEFINITIONS.map((d) => ({ ...d }));
  undoStack.length = 0;
  redoStack.length = 0;
  _algorithmSettings = null;
  _presets = null;
  _activePresetId = undefined;
  _snapshots = null;
  _activeSnapshotId = undefined;
  _participantSets = null;
  _activeParticipantSetId = undefined;
  _taskSets = null;
  _activeTaskSetId = undefined;
  _restRules = [];
  _masterDataRevision = 0;
  scheduleDate = defaultScheduleDate();
  scheduleDays = 7;
  liveModeState = { enabled: false, currentTimestamp: new Date() };
}

/**
 * Schedule a debounced save to localStorage.
 * Called from notify() so every store mutation triggers persistence.
 */
function debouncedSave(): void {
  // While wedged, skip debounced auto-saves to avoid repeated serialization
  // of data that will fail anyway. The wedge is cleared when an explicit
  // saveToStorage() call (or saveSchedule, etc.) succeeds.
  if (_storageWedged) return;
  if (_saveDebounceTimer) clearTimeout(_saveDebounceTimer);
  _saveDebounceTimer = setTimeout(() => {
    saveToStorage();
  }, SAVE_DEBOUNCE_MS);
}

/**
 * Flush any pending debounced save immediately.
 * Used by beforeunload to avoid losing the last mutation.
 */
export function flushPendingSave(): void {
  if (_saveDebounceTimer) {
    clearTimeout(_saveDebounceTimer);
    _saveDebounceTimer = null;
    saveToStorage();
  }
}

// ─── Algorithm Settings (separate from undo/redo) ────────────────────────────

const STORAGE_KEY_ALGORITHM = 'gardenmanager_algorithm';

let _algorithmSettings: AlgorithmSettings | null = null;

function _normalizeDayStartHour(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return DEFAULT_ALGORITHM_SETTINGS.dayStartHour;
  return Math.max(0, Math.min(23, Math.floor(v)));
}

/**
 * Get current algorithm settings (lazy-loaded from localStorage).
 * Returns a deep copy so mutations don't leak.
 */
export function getAlgorithmSettings(): AlgorithmSettings {
  if (!_algorithmSettings) {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ALGORITHM);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<AlgorithmSettings>;
        // Merge with defaults to handle any newly-added fields
        _algorithmSettings = {
          config: { ...DEFAULT_ALGORITHM_SETTINGS.config, ...(parsed.config || {}) },
          disabledHardConstraints: Array.isArray(parsed.disabledHardConstraints)
            ? (parsed.disabledHardConstraints as HardConstraintCode[])
            : [],
          dayStartHour: _normalizeDayStartHour(parsed.dayStartHour),
          splittingMode:
            parsed.splittingMode === 'off' ||
            parsed.splittingMode === 'feasibility' ||
            parsed.splittingMode === 'quality'
              ? parsed.splittingMode
              : DEFAULT_ALGORITHM_SETTINGS.splittingMode,
        };
      } else {
        _algorithmSettings = {
          config: { ...DEFAULT_ALGORITHM_SETTINGS.config },
          disabledHardConstraints: [...DEFAULT_ALGORITHM_SETTINGS.disabledHardConstraints],
          dayStartHour: DEFAULT_ALGORITHM_SETTINGS.dayStartHour,
          splittingMode: DEFAULT_ALGORITHM_SETTINGS.splittingMode,
        };
      }
    } catch {
      _algorithmSettings = {
        config: { ...DEFAULT_ALGORITHM_SETTINGS.config },
        disabledHardConstraints: [...DEFAULT_ALGORITHM_SETTINGS.disabledHardConstraints],
        dayStartHour: DEFAULT_ALGORITHM_SETTINGS.dayStartHour,
        splittingMode: DEFAULT_ALGORITHM_SETTINGS.splittingMode,
      };
    }
  }
  return {
    config: { ..._algorithmSettings.config },
    disabledHardConstraints: [..._algorithmSettings.disabledHardConstraints],
    dayStartHour: _algorithmSettings.dayStartHour,
    splittingMode: _algorithmSettings.splittingMode,
  };
}

/**
 * Update algorithm settings (partial merge). Persists immediately.
 * Does NOT fire notify() — changes take effect on next generate/revalidate.
 * Fires notifyAlgorithmChanged() so the UI can mark the schedule as stale.
 */
export function setAlgorithmSettings(patch: Partial<AlgorithmSettings>): void {
  const current = getAlgorithmSettings();
  const nextDayStartHour =
    patch.dayStartHour !== undefined ? _normalizeDayStartHour(patch.dayStartHour) : current.dayStartHour;
  const dayStartHourChanged = nextDayStartHour !== current.dayStartHour;
  _algorithmSettings = {
    config: patch.config ? { ...current.config, ...patch.config } : current.config,
    disabledHardConstraints:
      patch.disabledHardConstraints !== undefined
        ? [...patch.disabledHardConstraints]
        : current.disabledHardConstraints,
    dayStartHour: nextDayStartHour,
    splittingMode: patch.splittingMode !== undefined ? patch.splittingMode : current.splittingMode,
  };
  _saveAlgorithmSettings();
  // The op-day boundary anchors weekly blackout windows in `computeAvailability`,
  // so when it changes every participant's pre-expanded availability must be rebuilt.
  if (dayStartHourChanged) recalcAllAvailability();
  notifyAlgorithmChanged();
}

/**
 * Reset algorithm settings to factory defaults. Persists immediately.
 * Also sets the active preset to the built-in Default.
 */
export function resetAlgorithmSettings(): void {
  _algorithmSettings = {
    config: { ...DEFAULT_ALGORITHM_SETTINGS.config },
    disabledHardConstraints: [...DEFAULT_ALGORITHM_SETTINGS.disabledHardConstraints],
    dayStartHour: DEFAULT_ALGORITHM_SETTINGS.dayStartHour,
    splittingMode: DEFAULT_ALGORITHM_SETTINGS.splittingMode,
  };
  _saveAlgorithmSettings();
  // Also switch active preset to Default
  _initPresets(); // ensure loaded
  _activePresetId = DEFAULT_PRESET.id;
  _saveActivePresetId();
  notifyAlgorithmChanged();
}

/**
 * Get the configured day-start hour (0-23). Convenience shorthand for
 * `getAlgorithmSettings().dayStartHour`.
 */
export function getDayStartHour(): number {
  return getAlgorithmSettings().dayStartHour;
}

/**
 * Build a Set of disabled hard constraint codes for efficient lookup.
 */
export function getDisabledHCSet(): Set<string> {
  const settings = getAlgorithmSettings();
  return new Set(settings.disabledHardConstraints);
}

function _saveAlgorithmSettings(): boolean {
  if (!_algorithmSettings) return false;
  try {
    localStorage.setItem(STORAGE_KEY_ALGORITHM, JSON.stringify(_algorithmSettings));
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save algorithm settings:', err);
    reportSaveError(err);
    return false;
  }
}

// ─── Algorithm Presets ───────────────────────────────────────────────────────

const STORAGE_KEY_PRESETS = 'gardenmanager_algorithm_presets';
const STORAGE_KEY_ACTIVE_PRESET = 'gardenmanager_active_preset_id';

let _presets: AlgorithmPreset[] | null = null;
let _activePresetId: string | null | undefined; // undefined = not yet loaded

/**
 * Hook for tab-algorithm to register its debounce-flush function.
 * Called before any save/update operation to ensure pending slider
 * changes are applied to the working copy first.
 */
let _flushPendingWeightUpdate: (() => void) | null = null;

export function registerWeightFlush(fn: () => void): void {
  _flushPendingWeightUpdate = fn;
}

function _flushWeights(): void {
  if (_flushPendingWeightUpdate) _flushPendingWeightUpdate();
}

/** Lazily initialise presets from localStorage, with first-load migration. */
function _initPresets(): AlgorithmPreset[] {
  if (_presets) return _presets;

  const raw = localStorage.getItem(STORAGE_KEY_PRESETS);
  if (raw) {
    try {
      _presets = JSON.parse(raw) as AlgorithmPreset[];
      // Ensure the built-in Default preset always exists
      if (!_presets.find((p) => p.id === DEFAULT_PRESET.id)) {
        _presets.unshift(_deepCopyPreset(DEFAULT_PRESET));
      }
    } catch {
      _presets = [_deepCopyPreset(DEFAULT_PRESET)];
    }
  } else {
    _presets = [_deepCopyPreset(DEFAULT_PRESET)];
    _activePresetId = DEFAULT_PRESET.id;
    _savePresets();
    _saveActivePresetId();
  }

  // Load active preset id if not already set by migration
  if (_activePresetId === undefined) {
    _activePresetId = localStorage.getItem(STORAGE_KEY_ACTIVE_PRESET) || DEFAULT_PRESET.id;
  }

  return _presets;
}

function _deepCopyPreset(p: AlgorithmPreset): AlgorithmPreset {
  return {
    ...p,
    settings: {
      config: { ...p.settings.config },
      disabledHardConstraints: [...p.settings.disabledHardConstraints],
      dayStartHour: p.settings.dayStartHour ?? DEFAULT_ALGORITHM_SETTINGS.dayStartHour,
      splittingMode: p.settings.splittingMode ?? DEFAULT_ALGORITHM_SETTINGS.splittingMode,
    },
  };
}

function _savePresets(): boolean {
  if (!_presets) return false;
  try {
    localStorage.setItem(STORAGE_KEY_PRESETS, JSON.stringify(_presets));
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save algorithm presets:', err);
    reportSaveError(err);
    return false;
  }
}

function _saveActivePresetId(): boolean {
  try {
    if (_activePresetId) {
      localStorage.setItem(STORAGE_KEY_ACTIVE_PRESET, _activePresetId);
    } else {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET);
    }
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save active preset id:', err);
    reportSaveError(err);
    return false;
  }
}

/** Case-insensitive trimmed name duplicate check */
function _isPresetNameTaken(name: string, excludeId?: string): boolean {
  const norm = name.trim().toLowerCase();
  const presets = _initPresets();
  return presets.some((p) => p.name.trim().toLowerCase() === norm && p.id !== excludeId);
}

// ─── Preset Public API ──────────────────────────────────────────────────────

/** Get all presets (built-in first, then by createdAt) */
export function getAllPresets(): AlgorithmPreset[] {
  const presets = _initPresets();
  return presets
    .slice()
    .sort((a, b) => {
      if (a.builtIn && !b.builtIn) return -1;
      if (!a.builtIn && b.builtIn) return 1;
      return a.createdAt - b.createdAt;
    })
    .map((p) => _deepCopyPreset(p));
}

/** Get a single preset by id */
export function getPresetById(id: string): AlgorithmPreset | undefined {
  const presets = _initPresets();
  const found = presets.find((p) => p.id === id);
  return found ? _deepCopyPreset(found) : undefined;
}

/** Get the active preset id (may be null if none) */
export function getActivePresetId(): string | null {
  _initPresets(); // ensure loaded
  return _activePresetId ?? null;
}

/**
 * Load a preset into the working copy.
 * Replaces algorithm settings entirely (not a partial merge).
 */
export function loadPreset(id: string): void {
  const preset = getPresetById(id);
  if (!preset) return;
  // Full replacement — not partial merge
  _algorithmSettings = {
    config: { ...preset.settings.config },
    disabledHardConstraints: [...preset.settings.disabledHardConstraints],
    dayStartHour: _normalizeDayStartHour(preset.settings.dayStartHour),
    splittingMode: preset.settings.splittingMode ?? DEFAULT_ALGORITHM_SETTINGS.splittingMode,
  };
  _saveAlgorithmSettings();
  _activePresetId = id;
  _saveActivePresetId();
  notifyAlgorithmChanged();
}

/**
 * Save the current working copy as a new preset.
 * Returns the new preset, or null if the name is taken.
 */
export function saveCurrentAsPreset(name: string, description: string): AlgorithmPreset | null {
  _flushWeights();
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (_isPresetNameTaken(trimmed)) return null;

  const preset: AlgorithmPreset = {
    id: uid('preset'),
    name: trimmed,
    description: description.trim(),
    settings: getAlgorithmSettings(), // deep copy via getter
    createdAt: Date.now(),
  };

  const presets = _initPresets();
  const prevActiveId = _activePresetId;
  presets.push(preset);
  if (!_savePresets()) {
    presets.pop(); // rollback — keep in-memory and disk in sync
    return null;
  }

  _activePresetId = preset.id;
  if (!_saveActivePresetId()) {
    _activePresetId = prevActiveId; // partial rollback
  }

  return _deepCopyPreset(preset);
}

/**
 * Overwrite an existing preset's settings with the current working copy.
 * Returns false if preset not found or is built-in.
 */
export function updatePreset(id: string): boolean {
  _flushWeights();
  const presets = _initPresets();
  const idx = presets.findIndex((p) => p.id === id);
  if (idx === -1) return false;
  if (presets[idx].builtIn) return false;

  const prevSettings = presets[idx].settings;
  presets[idx].settings = getAlgorithmSettings(); // deep copy via getter
  if (!_savePresets()) {
    presets[idx].settings = prevSettings; // rollback
    return false;
  }
  return true;
}

/**
 * Rename a preset. Returns null on success, or an error string.
 */
export function renamePreset(id: string, name: string, description: string): string | null {
  const presets = _initPresets();
  const preset = presets.find((p) => p.id === id);
  if (!preset) return 'סט לא נמצא';
  if (preset.builtIn) return 'לא ניתן לשנות שם של סט מובנה';

  const trimmed = name.trim();
  if (!trimmed) return 'השם לא יכול להיות ריק';
  if (_isPresetNameTaken(trimmed, id)) return 'סט עם שם זה כבר קיים';

  const prevName = preset.name;
  const prevDesc = preset.description;
  preset.name = trimmed;
  preset.description = description.trim();
  if (!_savePresets()) {
    preset.name = prevName; // rollback
    preset.description = prevDesc;
    return 'שמירה נכשלה — נפח האחסון בדפדפן מלא';
  }
  return null;
}

/**
 * Duplicate a preset with a unique name.
 * Returns the new preset.
 */
export function duplicatePreset(id: string): AlgorithmPreset | null {
  const source = getPresetById(id);
  if (!source) return null;

  const presets = _initPresets();
  let newName = source.name + ' (עותק)';
  let attempt = 2;
  while (_isPresetNameTaken(newName)) {
    newName = `${source.name} (עותק ${attempt++})`;
  }

  const dup: AlgorithmPreset = {
    id: uid('preset'),
    name: newName,
    description: source.description,
    settings: source.settings, // already a deep copy from getPresetById
    builtIn: false,
    createdAt: Date.now(),
  };
  presets.push(dup);
  if (!_savePresets()) {
    presets.pop(); // rollback
    return null;
  }
  return _deepCopyPreset(dup);
}

/**
 * Delete a preset. If it was the active one, load the Default preset.
 * Returns false if preset not found or is built-in.
 */
export function deletePreset(id: string): boolean {
  const presets = _initPresets();
  const idx = presets.findIndex((p) => p.id === id);
  if (idx === -1) return false;
  if (presets[idx].builtIn) return false;

  const [removed] = presets.splice(idx, 1);
  if (!_savePresets()) {
    presets.splice(idx, 0, removed); // rollback
    return false;
  }

  if (_activePresetId === id) {
    loadPreset(DEFAULT_PRESET.id);
  }
  return true;
}

/**
 * Compare the current working copy against the active preset.
 * Returns true if they differ (preset is "dirty").
 * Survives page reloads because both sides come from localStorage.
 */
export function isPresetDirty(): boolean {
  const activeId = getActivePresetId();
  if (!activeId) return false;
  const preset = getPresetById(activeId);
  if (!preset) return false;
  return JSON.stringify(getAlgorithmSettings()) !== JSON.stringify(preset.settings);
}

// ─── Schedule Snapshots ─────────────────────────────────────────────────────

const STORAGE_KEY_SNAPSHOTS = 'gardenmanager_schedule_snapshots';
const STORAGE_KEY_ACTIVE_SNAPSHOT = 'gardenmanager_active_snapshot_id';
const MAX_SNAPSHOTS = 15;

let _snapshots: ScheduleSnapshot[] | null = null;
let _activeSnapshotId: string | null | undefined; // undefined = not yet loaded

/** Deep-copy a snapshot using JSON serialize round-trip (handles Dates). */
function _deepCopySnapshot(s: ScheduleSnapshot): ScheduleSnapshot {
  return jsonDeserialize<ScheduleSnapshot>(jsonSerialize(s));
}

/** Coerce tainted task colors AND participant ids on imported/persisted
 *  snapshots through their respective guards. A snapshot schedule can be planted
 *  verbatim by a tampered snapshot/full-backup import, so both the raw
 *  `style="…${task.color}…"` and `data-pid="${p.id}"` sinks it later feeds must
 *  be neutralised at this load/import chokepoint. */
function _sanitizeSnapshots(snaps: ScheduleSnapshot[]): void {
  for (const snap of snaps) {
    for (const task of snap.schedule?.tasks ?? []) {
      task.color = _safeColor(task.color);
    }
    // The frozen snapshot can also embed a continuitySnapshot whose assignment
    // colors bypass the task-color guard above — clean those too.
    if (snap.schedule?.continuitySnapshot) sanitizeContinuitySnapshotColors(snap.schedule.continuitySnapshot);
    _sanitizeScheduleParticipantIds(snap.schedule);
  }
}

function _initSnapshots(): ScheduleSnapshot[] {
  if (_snapshots) return _snapshots;

  const raw = localStorage.getItem(STORAGE_KEY_SNAPSHOTS);
  if (raw) {
    try {
      _snapshots = jsonDeserialize<ScheduleSnapshot[]>(raw);
      _sanitizeSnapshots(_snapshots);
    } catch {
      _snapshots = [];
    }
  } else {
    _snapshots = [];
  }

  // Load active snapshot id
  if (_activeSnapshotId === undefined) {
    _activeSnapshotId = localStorage.getItem(STORAGE_KEY_ACTIVE_SNAPSHOT) || null;
  }

  return _snapshots;
}

function _saveSnapshots(): boolean {
  if (!_snapshots) return false;
  try {
    const blob = jsonSerialize(_snapshots);
    console.log(`[Store] Persisting ${_snapshots.length} snapshot(s), ${(blob.length / 1024).toFixed(1)} KB`);
    localStorage.setItem(STORAGE_KEY_SNAPSHOTS, blob);
    onSaveSuccess();
    return true;
  } catch (err: unknown) {
    if (isQuotaExceededError(err)) {
      console.warn('[Store] localStorage quota exceeded for snapshots. Consider deleting old snapshots.');
    } else {
      console.error('[Store] Failed to save schedule snapshots:', err);
    }
    reportSaveError(err);
    return false;
  }
}

function _saveActiveSnapshotId(): boolean {
  try {
    if (_activeSnapshotId) {
      localStorage.setItem(STORAGE_KEY_ACTIVE_SNAPSHOT, _activeSnapshotId);
    } else {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_SNAPSHOT);
    }
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save active snapshot id:', err);
    reportSaveError(err);
    return false;
  }
}

/** Case-insensitive trimmed name duplicate check */
function _isSnapshotNameTaken(name: string, excludeId?: string): boolean {
  const norm = name.trim().toLowerCase();
  const snapshots = _initSnapshots();
  return snapshots.some((s) => s.name.trim().toLowerCase() === norm && s.id !== excludeId);
}

// ─── Snapshot Public API ────────────────────────────────────────────────────

/** Get all snapshots sorted by createdAt descending (newest first) */
export function getAllSnapshots(): ScheduleSnapshot[] {
  const snapshots = _initSnapshots();
  return snapshots
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((s) => _deepCopySnapshot(s));
}

/** Get a single snapshot by id */
export function getSnapshotById(id: string): ScheduleSnapshot | undefined {
  const snapshots = _initSnapshots();
  const found = snapshots.find((s) => s.id === id);
  return found ? _deepCopySnapshot(found) : undefined;
}

/** Get the active snapshot id (may be null if none) */
export function getActiveSnapshotId(): string | null {
  _initSnapshots(); // ensure loaded
  return _activeSnapshotId ?? null;
}

/** Set the active snapshot id */
export function setActiveSnapshotId(id: string | null): void {
  _initSnapshots(); // ensure loaded
  _activeSnapshotId = id;
  _saveActiveSnapshotId();
}

/**
 * Save a schedule as a new named snapshot.
 * Returns the new snapshot, or null if the name is taken or limit reached,
 * or 'storage-full' if localStorage quota was exceeded.
 */
export function saveScheduleAsSnapshot(
  schedule: Schedule,
  name: string,
  description: string,
): ScheduleSnapshot | 'storage-full' | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (trimmed.length > 100) return null;
  if (_isSnapshotNameTaken(trimmed)) return null;

  const snapshots = _initSnapshots();
  if (snapshots.length >= MAX_SNAPSHOTS) {
    console.warn(`[Store] Snapshot limit reached (${MAX_SNAPSHOTS}). Delete older snapshots first.`);
    return null;
  }

  // Deep-copy the schedule via JSON round-trip. This can fail if the
  // schedule contains invalid Dates (toISOString → RangeError) or other
  // non-serializable values. Catch and report rather than crashing.
  let deepCopiedSchedule: Schedule;
  try {
    const serialized = jsonSerialize(schedule);
    console.log(`[Store] Snapshot schedule serialized: ${(serialized.length / 1024).toFixed(1)} KB`);
    deepCopiedSchedule = jsonDeserialize<Schedule>(serialized);
  } catch (err) {
    console.error('[Store] Failed to deep-copy schedule for snapshot — serialization error:', err);
    reportSaveError(err);
    return 'storage-full';
  }

  const snapshot: ScheduleSnapshot = {
    id: uid('snap'),
    name: trimmed,
    description: description.trim(),
    schedule: deepCopiedSchedule,
    createdAt: Date.now(),
  };

  snapshots.push(snapshot);
  if (!_saveSnapshots()) {
    snapshots.pop(); // rollback
    return 'storage-full';
  }

  _activeSnapshotId = snapshot.id;
  _saveActiveSnapshotId();

  return _deepCopySnapshot(snapshot);
}

/**
 * Overwrite an existing snapshot's schedule. Algorithm settings are embedded
 * in the schedule itself (see Schedule.algorithmSettings).
 * Returns false if snapshot not found or is built-in.
 */
export function updateSnapshot(id: string, schedule: Schedule): boolean {
  const snapshots = _initSnapshots();
  const idx = snapshots.findIndex((s) => s.id === id);
  if (idx === -1) return false;
  if (snapshots[idx].builtIn) return false;

  const oldSchedule = snapshots[idx].schedule;

  snapshots[idx].schedule = jsonDeserialize<Schedule>(jsonSerialize(schedule));
  if (!_saveSnapshots()) {
    snapshots[idx].schedule = oldSchedule; // rollback
    return false;
  }
  return true;
}

/**
 * Rename a snapshot. Returns null on success, or an error string.
 */
export function renameSnapshot(id: string, name: string, description: string): string | null {
  const snapshots = _initSnapshots();
  const snapshot = snapshots.find((s) => s.id === id);
  if (!snapshot) return 'תמונת מצב לא נמצאה';
  if (snapshot.builtIn) return 'לא ניתן לשנות שם של תמונת מצב מובנית';

  const trimmed = name.trim();
  if (!trimmed) return 'השם לא יכול להיות ריק';
  if (trimmed.length > 100) return 'השם ארוך מדי (עד 100 תווים)';
  if (_isSnapshotNameTaken(trimmed, id)) return 'תמונת מצב עם שם זה כבר קיימת';

  const oldName = snapshot.name;
  const oldDesc = snapshot.description;
  snapshot.name = trimmed;
  snapshot.description = description.trim();
  if (!_saveSnapshots()) {
    snapshot.name = oldName; // rollback
    snapshot.description = oldDesc;
    return 'שמירה נכשלה — נפח האחסון בדפדפן מלא';
  }
  return null;
}

/**
 * Duplicate a snapshot with a unique name.
 * Returns the new snapshot.
 */
export function duplicateSnapshot(id: string): ScheduleSnapshot | null {
  const source = getSnapshotById(id);
  if (!source) return null;

  const snapshots = _initSnapshots();
  if (snapshots.length >= MAX_SNAPSHOTS) return null;

  let newName = source.name + ' (עותק)';
  let attempt = 2;
  while (_isSnapshotNameTaken(newName)) {
    newName = `${source.name} (עותק ${attempt++})`;
  }

  const dup: ScheduleSnapshot = {
    id: uid('snap'),
    name: newName,
    description: source.description,
    schedule: source.schedule, // already a deep copy from getSnapshotById; embeds algorithmSettings
    builtIn: false,
    createdAt: Date.now(),
  };
  snapshots.push(dup);
  if (!_saveSnapshots()) {
    snapshots.pop(); // rollback
    return null;
  }
  return _deepCopySnapshot(dup);
}

/**
 * Delete a snapshot. If it was the active one, clears active snapshot.
 * Returns false if snapshot not found or is built-in.
 */
export function deleteSnapshot(id: string): boolean {
  const snapshots = _initSnapshots();
  const idx = snapshots.findIndex((s) => s.id === id);
  if (idx === -1) return false;
  if (snapshots[idx].builtIn) return false;

  const [removed] = snapshots.splice(idx, 1);
  if (!_saveSnapshots()) {
    snapshots.splice(idx, 0, removed); // rollback
    return false;
  }

  if (_activeSnapshotId === id) {
    _activeSnapshotId = null;
    _saveActiveSnapshotId();
  }
  return true;
}

/** Get the maximum number of snapshots allowed */
export function getMaxSnapshots(): number {
  return MAX_SNAPSHOTS;
}

// ─── Participant Sets ────────────────────────────────────────────────────────

const STORAGE_KEY_PSETS = 'gardenmanager_participant_sets';
const STORAGE_KEY_ACTIVE_PSET = 'gardenmanager_active_participant_set_id';
const MAX_PARTICIPANT_SETS = 30;

let _participantSets: ParticipantSet[] | null = null;
let _activeParticipantSetId: string | null | undefined; // undefined = not yet loaded

/** Deep-copy a ParticipantSet via JSON round-trip. */
function _deepCopyPSet(s: ParticipantSet): ParticipantSet {
  return JSON.parse(JSON.stringify(s)) as ParticipantSet;
}

function _normalizeParticipantSet(pset: ParticipantSet): ParticipantSet {
  const pakalCatalog = normalizePakalDefinitions(pset.pakalCatalog);
  const certificationCatalog = normalizeCertificationDefinitions(pset.certificationCatalog);
  const participants = (pset.participants || []).map((snap) => {
    // Clamp the fair-share multiplier at the normalize chokepoint so the STORED
    // set (and any subsequent re-export) is already bounded — an untrusted
    // .gm.json import lands here via importParticipantSetDirect. Normalize the
    // neutral default back to undefined (the "store only when ≠ 1" convention).
    const wm = clampWorkloadMultiplier(snap.workloadMultiplier);
    return {
      ...snap,
      pakalIds: sanitizePakalIds(snap.pakalIds, pakalCatalog),
      // Certification ids are intentionally PRESERVED even when orphaned/deleted —
      // the cert soft-delete model keeps them on the participant and surfaces them via
      // the ⚠ orphan badge. This matches the live-store load paths (loadFromStorage,
      // restoreSnapshot, loadParticipantSet). Only pakalIds are hard-sanitized at load.
      certifications: Array.isArray(snap.certifications) ? [...snap.certifications] : [],
      workloadMultiplier: wm === 1 ? undefined : wm,
    };
  });

  return {
    ...pset,
    pakalCatalog,
    certificationCatalog,
    participants:
      pset.id === 'pset-default' && needsDefaultL0PakalSeed(participants)
        ? applyDefaultL0PakalSeed(participants)
        : participants,
  };
}

/** Lazily initialise participant sets from localStorage. */
function _initParticipantSets(): ParticipantSet[] {
  if (_participantSets) return _participantSets;

  const raw = localStorage.getItem(STORAGE_KEY_PSETS);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as ParticipantSet[];
      _participantSets = Array.isArray(parsed) ? parsed.map(_normalizeParticipantSet) : [];
      const shouldPersistMigration =
        Array.isArray(parsed) &&
        parsed.some((pset) => pset.id === 'pset-default' && needsDefaultL0PakalSeed(pset.participants || []));
      if (shouldPersistMigration) _saveParticipantSets();
    } catch {
      _participantSets = [];
    }
  } else {
    // First load — create built-in set from current participants (demo data)
    _participantSets = [];
    _seedBuiltInParticipantSet();
  }

  // Load active set id
  if (_activeParticipantSetId === undefined) {
    _activeParticipantSetId = localStorage.getItem(STORAGE_KEY_ACTIVE_PSET) || null;
  }

  return _participantSets;
}

/** Snapshot the current in-memory participants into a ParticipantSnapshot array.
 *  IDs are stripped from dateUnavailability rules so that save→load→dirty-check
 *  comparisons work correctly (loaded rules get fresh IDs). */
function _snapshotCurrentParticipants(): ParticipantSnapshot[] {
  const all = getAllParticipants();
  return all.map((p) => {
    const dateRules = getDateUnavailabilities(p.id);
    return {
      name: p.name,
      level: p.level,
      certifications: [...p.certifications],
      group: p.group,
      dateUnavailability: dateRules.map(({ id: _, ...rest }) => rest),
      notWithIds:
        getNotWithIds(p.id).length > 0
          ? getNotWithIds(p.id)
              .map((id) => participants.get(id)?.name)
              .filter((n): n is string => !!n)
          : undefined,
      pakalIds: sanitizePakalIds(p.pakalIds, pakalDefinitions),
      preferredTaskName: p.preferredTaskName,
      lessPreferredTaskName: p.lessPreferredTaskName,
      workloadMultiplier: p.workloadMultiplier,
    };
  });
}

function _snapshotCurrentPakalCatalog(participantSnapshots: ParticipantSnapshot[]): PakalDefinition[] {
  const referencedIds = new Set<string>();
  for (const snap of participantSnapshots) {
    for (const pakalId of snap.pakalIds || []) referencedIds.add(pakalId);
  }
  return getPakalDefinitions().filter((def) => referencedIds.has(def.id));
}

function _snapshotCurrentCertificationCatalog(participantSnapshots: ParticipantSnapshot[]): CertificationDefinition[] {
  const referencedIds = new Set<string>();
  for (const snap of participantSnapshots) {
    for (const certId of snap.certifications) referencedIds.add(certId);
  }
  return certificationDefinitions.filter((def) => referencedIds.has(def.id)).map((def) => ({ ...def }));
}

/** Create the built-in default set from whatever participants are currently loaded. */
function _seedBuiltInParticipantSet(): void {
  const sets = _participantSets!;
  if (sets.find((s) => s.id === 'pset-default')) return;
  const snap = _snapshotCurrentParticipants();
  const pakalCatalog = _snapshotCurrentPakalCatalog(snap);
  if (snap.length === 0) return; // don't seed empty
  sets.unshift({
    id: 'pset-default',
    name: 'סט ברירת מחדל',
    description: 'המשתתפים המקוריים',
    participants: snap,
    pakalCatalog,
    certificationCatalog: _snapshotCurrentCertificationCatalog(snap),
    builtIn: true,
    createdAt: 0,
  });
  _saveParticipantSets();
}

function _saveParticipantSets(): boolean {
  if (!_participantSets) return false;
  try {
    localStorage.setItem(STORAGE_KEY_PSETS, JSON.stringify(_participantSets));
    onSaveSuccess();
    return true;
  } catch (err: unknown) {
    if (isQuotaExceededError(err)) {
      console.warn('[Store] localStorage quota exceeded for participant sets.');
    } else {
      console.warn('[Store] Failed to save participant sets:', err);
    }
    reportSaveError(err);
    return false;
  }
}

function _saveActiveParticipantSetId(): boolean {
  try {
    if (_activeParticipantSetId) {
      localStorage.setItem(STORAGE_KEY_ACTIVE_PSET, _activeParticipantSetId);
    } else {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_PSET);
    }
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save active participant set id:', err);
    reportSaveError(err);
    return false;
  }
}

/** Case-insensitive trimmed name duplicate check */
function _isPSetNameTaken(name: string, excludeId?: string): boolean {
  const norm = name.trim().toLowerCase();
  const sets = _initParticipantSets();
  return sets.some((s) => s.name.trim().toLowerCase() === norm && s.id !== excludeId);
}

// ─── Participant Sets Public API ─────────────────────────────────────────────

/** Get all participant sets (built-in first, then by createdAt) */
export function getAllParticipantSets(): ParticipantSet[] {
  const sets = _initParticipantSets();
  return sets
    .slice()
    .sort((a, b) => {
      if (a.builtIn && !b.builtIn) return -1;
      if (!a.builtIn && b.builtIn) return 1;
      return a.createdAt - b.createdAt;
    })
    .map((s) => _deepCopyPSet(s));
}

/** Get a single participant set by id */
export function getParticipantSetById(id: string): ParticipantSet | undefined {
  const sets = _initParticipantSets();
  const found = sets.find((s) => s.id === id);
  return found ? _deepCopyPSet(found) : undefined;
}

/** Get the active participant set id (may be null) */
export function getActiveParticipantSetId(): string | null {
  _initParticipantSets();
  return _activeParticipantSetId ?? null;
}

/**
 * Save the current participants as a new named set.
 * Returns the new set, or null if the name is taken or limit reached.
 */
export function saveCurrentAsParticipantSet(name: string, description: string): ParticipantSet | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (_isPSetNameTaken(trimmed)) return null;

  const sets = _initParticipantSets();
  if (sets.length >= MAX_PARTICIPANT_SETS) {
    console.warn(`[Store] Participant set limit reached (${MAX_PARTICIPANT_SETS}).`);
    return null;
  }

  const participantsSnapshot = _snapshotCurrentParticipants();

  const pset: ParticipantSet = {
    id: uid('pset'),
    name: trimmed,
    description: description.trim(),
    participants: participantsSnapshot,
    pakalCatalog: _snapshotCurrentPakalCatalog(participantsSnapshot),
    certificationCatalog: _snapshotCurrentCertificationCatalog(participantsSnapshot),
    createdAt: Date.now(),
  };

  const prevActiveId = _activeParticipantSetId;
  sets.push(pset);
  if (!_saveParticipantSets()) {
    sets.pop(); // rollback
    return null;
  }

  _activeParticipantSetId = pset.id;
  if (!_saveActiveParticipantSetId()) {
    _activeParticipantSetId = prevActiveId;
  }

  return _deepCopyPSet(pset);
}

/**
 * Load a participant set — replaces ALL current participants.
 * This is a single undoable action.
 */
export function loadParticipantSet(id: string): void {
  const pset = getParticipantSetById(id);
  if (!pset) return;

  pushSnapshot();
  _suppressSnapshot = true;
  try {
    // Clear all current participants
    participants.clear();
    dateUnavailabilities.clear();
    ensurePakalDefinitions(pset.pakalCatalog || []);
    ensureCertificationDefinitions(pset.certificationCatalog);

    // Add participants from the set
    for (const snap of pset.participants) {
      const pid = uid('p');
      const certs = [...snap.certifications];
      const pIds = sanitizePakalIds(snap.pakalIds, pakalDefinitions);
      const p: Participant = {
        id: pid,
        name: snap.name,
        level: snap.level,
        certifications: certs,
        pakalIds: pIds,
        group: snap.group,
        availability: getDefaultAvailability(),
        dateUnavailability: [],
        preferredTaskName: snap.preferredTaskName,
        lessPreferredTaskName: snap.lessPreferredTaskName,
      };
      // Clamp at the activation boundary (see clampWorkloadMultiplier); the set
      // may have been imported from an untrusted .gm.json that bypassed the UI.
      const wm = clampWorkloadMultiplier(snap.workloadMultiplier);
      if (wm !== 1) p.workloadMultiplier = wm;
      participants.set(pid, p);

      // Restore date unavailabilities
      if (snap.dateUnavailability && snap.dateUnavailability.length > 0) {
        const rules: DateUnavailability[] = snap.dateUnavailability
          .map((r) => normalizeDateUnavailabilityRule(r))
          .filter((rule): rule is Omit<DateUnavailability, 'id'> => !!rule)
          .map((rule) => ({
            ...rule,
            id: uid('du'),
          }));
        if (rules.length > 0) {
          dateUnavailabilities.set(pid, rules);
          p.dateUnavailability = rules;
        }
      }
    }

    // Rebuild notWithPairs from snapshot notWithIds (stored as names, resolved to new IDs)
    notWithPairs.clear();
    // Build name→newId lookup from the participants we just created
    const nameToId = new Map<string, string>();
    for (const p of participants.values()) nameToId.set(p.name, p.id);
    for (const snap of pset.participants) {
      if (!snap.notWithIds || snap.notWithIds.length === 0) continue;
      const pid = nameToId.get(snap.name);
      if (!pid) continue;
      for (const targetName of snap.notWithIds) {
        const targetId = nameToId.get(targetName);
        if (!targetId || targetId === pid) continue;
        if (!notWithPairs.has(pid)) notWithPairs.set(pid, new Set());
        if (!notWithPairs.has(targetId)) notWithPairs.set(targetId, new Set());
        notWithPairs.get(pid)!.add(targetId);
        notWithPairs.get(targetId)!.add(pid);
      }
    }
    syncNotWithToParticipants();

    recalcAllAvailability();
  } finally {
    _suppressSnapshot = false;
  }

  _activeParticipantSetId = id;
  _saveActiveParticipantSetId();
  notify();
}

/**
 * Overwrite an existing set's participants with the current state.
 * Returns false if set not found or is built-in.
 */
export function updateParticipantSet(id: string): boolean {
  const sets = _initParticipantSets();
  const idx = sets.findIndex((s) => s.id === id);
  if (idx === -1) return false;
  if (sets[idx].builtIn) return false;

  const participantsSnapshot = _snapshotCurrentParticipants();
  const prevParticipants = sets[idx].participants;
  const prevPakal = sets[idx].pakalCatalog;
  const prevCerts = sets[idx].certificationCatalog;
  sets[idx].participants = participantsSnapshot;
  sets[idx].pakalCatalog = _snapshotCurrentPakalCatalog(participantsSnapshot);
  sets[idx].certificationCatalog = _snapshotCurrentCertificationCatalog(participantsSnapshot);
  if (!_saveParticipantSets()) {
    sets[idx].participants = prevParticipants; // rollback
    sets[idx].pakalCatalog = prevPakal;
    sets[idx].certificationCatalog = prevCerts;
    return false;
  }
  return true;
}

/**
 * Rename a participant set. Returns null on success, or an error string.
 */
export function renameParticipantSet(id: string, name: string, description: string): string | null {
  const sets = _initParticipantSets();
  const pset = sets.find((s) => s.id === id);
  if (!pset) return 'סט לא נמצא';
  if (pset.builtIn) return 'לא ניתן לשנות שם של סט מובנה';

  const trimmed = name.trim();
  if (!trimmed) return 'השם לא יכול להיות ריק';
  if (_isPSetNameTaken(trimmed, id)) return 'סט עם שם זה כבר קיים';

  const prevName = pset.name;
  const prevDesc = pset.description;
  pset.name = trimmed;
  pset.description = description.trim();
  if (!_saveParticipantSets()) {
    pset.name = prevName; // rollback
    pset.description = prevDesc;
    return 'שמירה נכשלה — נפח האחסון בדפדפן מלא';
  }
  return null;
}

/**
 * Duplicate a participant set with a unique name.
 */
export function duplicateParticipantSet(id: string): ParticipantSet | null {
  const source = getParticipantSetById(id);
  if (!source) return null;

  const sets = _initParticipantSets();
  if (sets.length >= MAX_PARTICIPANT_SETS) return null;

  let newName = source.name + ' (עותק)';
  let attempt = 2;
  while (_isPSetNameTaken(newName)) {
    newName = `${source.name} (עותק ${attempt++})`;
  }

  const dup: ParticipantSet = {
    id: uid('pset'),
    name: newName,
    description: source.description,
    participants: source.participants, // already deep-copied by getParticipantSetById
    pakalCatalog: source.pakalCatalog,
    certificationCatalog: source.certificationCatalog,
    builtIn: false,
    createdAt: Date.now(),
  };
  sets.push(dup);
  if (!_saveParticipantSets()) {
    sets.pop(); // rollback
    return null;
  }
  return _deepCopyPSet(dup);
}

/**
 * Delete a participant set. If it was active, clears the active id.
 * Returns false if not found or is built-in.
 */
export function deleteParticipantSet(id: string): boolean {
  const sets = _initParticipantSets();
  const idx = sets.findIndex((s) => s.id === id);
  if (idx === -1) return false;
  if (sets[idx].builtIn) return false;

  const [removed] = sets.splice(idx, 1);
  if (!_saveParticipantSets()) {
    sets.splice(idx, 0, removed); // rollback
    return false;
  }

  if (_activeParticipantSetId === id) {
    _activeParticipantSetId = null;
    _saveActiveParticipantSetId();
  }
  return true;
}

/**
 * Compare the current participants against the active set.
 * Returns true if they differ (set is "dirty").
 */
export function isParticipantSetDirty(): boolean {
  const activeId = getActiveParticipantSetId();
  if (!activeId) return false;
  const pset = getParticipantSetById(activeId);
  if (!pset) return false;
  const participantsSnapshot = _snapshotCurrentParticipants();
  const current = {
    participants: participantsSnapshot,
    pakalCatalog: _snapshotCurrentPakalCatalog(participantsSnapshot),
    certificationCatalog: _snapshotCurrentCertificationCatalog(participantsSnapshot),
  };
  return (
    JSON.stringify(current) !==
    JSON.stringify({
      participants: pset.participants,
      pakalCatalog: pset.pakalCatalog || normalizePakalDefinitions(undefined),
      certificationCatalog: pset.certificationCatalog,
    })
  );
}

/** Get the maximum number of participant sets allowed */
export function getMaxParticipantSets(): number {
  return MAX_PARTICIPANT_SETS;
}

// ─── Task Sets ───────────────────────────────────────────────────────────────

const STORAGE_KEY_TASK_SETS = 'gardenmanager_task_sets';
const STORAGE_KEY_ACTIVE_TASK_SET = 'gardenmanager_active_task_set_id';
const MAX_TASK_SETS = 30;

let _taskSets: TaskSet[] | null = null;
let _activeTaskSetId: string | null | undefined; // undefined = not yet loaded

/** Deep-copy a TaskSet via JSON round-trip. */
function _deepCopyTaskSet(s: TaskSet): TaskSet {
  return JSON.parse(JSON.stringify(s)) as TaskSet;
}

function _snapshotCurrentTaskSetState(): Pick<TaskSet, 'templates' | 'oneTimeTasks' | 'restRules'> {
  return {
    templates: _snapshotCurrentTaskTemplates(),
    oneTimeTasks: _snapshotCurrentOneTimeTasks(),
    restRules: _restRules.map((r) => ({ ...r })),
  };
}

/** Lazily initialise task sets from localStorage. */
/** Coerce tainted template/one-time colors on imported/persisted task sets through the hex guard. */
function _sanitizeTaskSetColors(sets: TaskSet[]): void {
  for (const set of sets) {
    for (const tpl of set.templates ?? []) tpl.color = _safeColor(tpl.color);
    for (const ot of set.oneTimeTasks ?? []) ot.color = _safeColor(ot.color);
  }
}

function _initTaskSets(): TaskSet[] {
  if (_taskSets) return _taskSets;

  const raw = localStorage.getItem(STORAGE_KEY_TASK_SETS);
  if (raw) {
    let parsed = false;
    try {
      _taskSets = JSON.parse(raw) as TaskSet[];
      _sanitizeTaskSetColors(_taskSets);
      parsed = true;
    } catch {
      _taskSets = [];
    }
    // Existing installs: add built-ins introduced after their first load.
    if (parsed && _ensureBuiltInTaskSets()) _saveTaskSets();
  } else {
    // First load — create built-in set from current templates
    _taskSets = [];
    _seedBuiltInTaskSet();
  }

  // Load active set id
  if (_activeTaskSetId === undefined) {
    _activeTaskSetId = localStorage.getItem(STORAGE_KEY_ACTIVE_TASK_SET) || null;
  }

  return _taskSets;
}

/** Snapshot the current in-memory task templates. */
function _snapshotCurrentTaskTemplates(): TaskTemplate[] {
  const all = getAllTaskTemplates();
  return JSON.parse(JSON.stringify(all)) as TaskTemplate[];
}

/** Snapshot the current in-memory one-time tasks. */
function _snapshotCurrentOneTimeTasks(): OneTimeTask[] {
  const all = getAllOneTimeTasks();
  return JSON.parse(JSON.stringify(all)) as OneTimeTask[];
}

// Built-in task sets. "מכרסם" keeps the legacy `tset-default` id so existing
// installs (and their active-set pointer) carry over. createdAt orders the
// built-ins in the list (getAllTaskSets sorts built-ins by it).
const TASK_SET_MEKARSEM_ID = 'tset-default';
const TASK_SET_MEKARSEM_NAME = 'מכרסם';
const TASK_SET_ASEFA_ID = 'tset-asefa';
const TASK_SET_NAHAR_AVIV_ID = 'tset-nahar-aviv';

/**
 * Create the built-in "מכרסם" set from whatever templates are currently loaded
 * (the default seed on a fresh install), add the other built-ins, and make
 * "מכרסם" the active set.
 */
function _seedBuiltInTaskSet(): void {
  const sets = _taskSets!;
  if (sets.find((s) => s.id === TASK_SET_MEKARSEM_ID)) return;
  const snapshot = _snapshotCurrentTaskSetState();
  if (snapshot.templates.length === 0) return; // don't seed empty
  sets.unshift({
    id: TASK_SET_MEKARSEM_ID,
    name: TASK_SET_MEKARSEM_NAME,
    description: '',
    ...snapshot,
    builtIn: true,
    createdAt: 0,
  });
  _ensureBuiltInTaskSets();
  _saveTaskSets();
  // The loaded templates ARE this set — mark it active unless a pointer exists.
  if (!localStorage.getItem(STORAGE_KEY_ACTIVE_TASK_SET)) {
    _activeTaskSetId = TASK_SET_MEKARSEM_ID;
    _saveActiveTaskSetId();
  }
}

/**
 * Bring the built-in sets up to date: name "מכרסם", add "אספה" (a copy of
 * מכרסם) and "נהר אביב" when missing. Returns true if anything changed.
 */
function _ensureBuiltInTaskSets(): boolean {
  const sets = _taskSets!;
  let changed = false;
  const mekarsem = sets.find((s) => s.id === TASK_SET_MEKARSEM_ID);
  if (mekarsem && mekarsem.name !== TASK_SET_MEKARSEM_NAME) {
    mekarsem.name = TASK_SET_MEKARSEM_NAME;
    mekarsem.description = '';
    changed = true;
  }
  if (mekarsem && !sets.some((s) => s.id === TASK_SET_ASEFA_ID)) {
    const copy = _deepCopyTaskSet(mekarsem);
    sets.push({
      ...copy,
      id: TASK_SET_ASEFA_ID,
      name: 'אספה',
      description: '',
      restRules: copy.restRules ?? [],
      builtIn: true,
      createdAt: 1,
    });
    changed = true;
  }
  if (!sets.some((s) => s.id === TASK_SET_NAHAR_AVIV_ID)) {
    sets.push({
      id: TASK_SET_NAHAR_AVIV_ID,
      name: 'נהר אביב',
      description: '',
      ..._buildNaharAvivTaskSetState(),
      builtIn: true,
      createdAt: 2,
    });
    changed = true;
  }
  return changed;
}

function _saveTaskSets(): boolean {
  if (!_taskSets) return false;
  try {
    localStorage.setItem(STORAGE_KEY_TASK_SETS, JSON.stringify(_taskSets));
    onSaveSuccess();
    return true;
  } catch (err: unknown) {
    if (isQuotaExceededError(err)) {
      console.warn('[Store] localStorage quota exceeded for task sets.');
    } else {
      console.warn('[Store] Failed to save task sets:', err);
    }
    reportSaveError(err);
    return false;
  }
}

function _saveActiveTaskSetId(): boolean {
  try {
    if (_activeTaskSetId) {
      localStorage.setItem(STORAGE_KEY_ACTIVE_TASK_SET, _activeTaskSetId);
    } else {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_TASK_SET);
    }
    onSaveSuccess();
    return true;
  } catch (err) {
    console.warn('[Store] Failed to save active task set id:', err);
    reportSaveError(err);
    return false;
  }
}

/** Case-insensitive trimmed name duplicate check */
function _isTaskSetNameTaken(name: string, excludeId?: string): boolean {
  const norm = name.trim().toLowerCase();
  const sets = _initTaskSets();
  return sets.some((s) => s.name.trim().toLowerCase() === norm && s.id !== excludeId);
}

// ─── Task Set Public API ─────────────────────────────────────────────────────

/** Get all task sets (built-in first, then by createdAt) */
export function getAllTaskSets(): TaskSet[] {
  const sets = _initTaskSets();
  return sets
    .slice()
    .sort((a, b) => {
      if (a.builtIn && !b.builtIn) return -1;
      if (!a.builtIn && b.builtIn) return 1;
      return a.createdAt - b.createdAt;
    })
    .map((s) => _deepCopyTaskSet(s));
}

/** Get a single task set by id */
export function getTaskSetById(id: string): TaskSet | undefined {
  const sets = _initTaskSets();
  const found = sets.find((s) => s.id === id);
  return found ? _deepCopyTaskSet(found) : undefined;
}

/** Get the active task set id (may be null) */
export function getActiveTaskSetId(): string | null {
  _initTaskSets();
  return _activeTaskSetId ?? null;
}

/**
 * Save the current task templates as a new named set.
 * Returns the new set, or null if the name is taken or limit reached.
 */
export function saveCurrentAsTaskSet(name: string, description: string): TaskSet | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (_isTaskSetNameTaken(trimmed)) return null;

  const sets = _initTaskSets();
  if (sets.length >= MAX_TASK_SETS) {
    console.warn(`[Store] Task set limit reached (${MAX_TASK_SETS}).`);
    return null;
  }

  const snapshot = _snapshotCurrentTaskSetState();
  const tset: TaskSet = {
    id: uid('tset'),
    name: trimmed,
    description: description.trim(),
    ...snapshot,
    createdAt: Date.now(),
  };

  const prevActiveId = _activeTaskSetId;
  sets.push(tset);
  if (!_saveTaskSets()) {
    sets.pop(); // rollback
    return null;
  }

  _activeTaskSetId = tset.id;
  if (!_saveActiveTaskSetId()) {
    _activeTaskSetId = prevActiveId;
  }

  return _deepCopyTaskSet(tset);
}

/**
 * Load a task set — replaces task templates, one-time tasks,
 * and category-break settings as a single snapshot.
 * This is a single undoable action.
 */
export function loadTaskSet(id: string): void {
  const tset = getTaskSetById(id);
  if (!tset) return;

  pushSnapshot();
  _suppressSnapshot = true;
  try {
    // Clear all current templates
    taskTemplates.clear();

    // Add templates from the set. Clamp numeric fields at this import boundary
    // (a saved/tampered set bypasses the UI clamps) — see loadFromStorage.
    for (const tpl of tset.templates) {
      const restored: TaskTemplate = JSON.parse(JSON.stringify(tpl));
      taskTemplates.set(restored.id, sanitizeTemplateNumericFields(restored));
    }

    oneTimeTasks.clear();
    for (const ot of tset.oneTimeTasks) {
      const restored: OneTimeTask = JSON.parse(JSON.stringify(ot));
      restored.scheduledDate = new Date(restored.scheduledDate);
      oneTimeTasks.set(restored.id, sanitizeTemplateNumericFields(restored));
    }

    _restRules = Array.isArray(tset.restRules) ? tset.restRules.map((r) => ({ ...r })) : [];
  } finally {
    _suppressSnapshot = false;
  }

  _activeTaskSetId = id;
  _saveActiveTaskSetId();
  notify();
}

/**
 * Overwrite an existing set's templates with the current state.
 * Returns false if set not found or is built-in.
 */
export function updateTaskSet(id: string): boolean {
  const sets = _initTaskSets();
  const idx = sets.findIndex((s) => s.id === id);
  if (idx === -1) return false;
  if (sets[idx].builtIn) return false;

  const snapshot = _snapshotCurrentTaskSetState();
  const prevTemplates = sets[idx].templates;
  const prevOneTime = sets[idx].oneTimeTasks;
  const prevRules = sets[idx].restRules;
  sets[idx].templates = snapshot.templates;
  sets[idx].oneTimeTasks = snapshot.oneTimeTasks;
  sets[idx].restRules = snapshot.restRules;
  if (!_saveTaskSets()) {
    sets[idx].templates = prevTemplates; // rollback
    sets[idx].oneTimeTasks = prevOneTime;
    sets[idx].restRules = prevRules;
    return false;
  }
  return true;
}

/**
 * Rename a task set. Returns null on success, or an error string.
 */
export function renameTaskSet(id: string, name: string, description: string): string | null {
  const sets = _initTaskSets();
  const tset = sets.find((s) => s.id === id);
  if (!tset) return 'סט לא נמצא';
  if (tset.builtIn) return 'לא ניתן לשנות שם של סט מובנה';

  const trimmed = name.trim();
  if (!trimmed) return 'השם לא יכול להיות ריק';
  if (_isTaskSetNameTaken(trimmed, id)) return 'סט עם שם זה כבר קיים';

  const prevName = tset.name;
  const prevDesc = tset.description;
  tset.name = trimmed;
  tset.description = description.trim();
  if (!_saveTaskSets()) {
    tset.name = prevName; // rollback
    tset.description = prevDesc;
    return 'שמירה נכשלה — נפח האחסון בדפדפן מלא';
  }
  return null;
}

/**
 * Duplicate a task set with a unique name.
 */
export function duplicateTaskSet(id: string): TaskSet | null {
  const source = getTaskSetById(id);
  if (!source) return null;

  const sets = _initTaskSets();
  if (sets.length >= MAX_TASK_SETS) return null;

  let newName = source.name + ' (עותק)';
  let attempt = 2;
  while (_isTaskSetNameTaken(newName)) {
    newName = `${source.name} (עותק ${attempt++})`;
  }

  const dup: TaskSet = {
    id: uid('tset'),
    name: newName,
    description: source.description,
    templates: source.templates, // already deep-copied by getTaskSetById
    oneTimeTasks: source.oneTimeTasks, // already deep-copied by getTaskSetById
    restRules: source.restRules,
    builtIn: false,
    createdAt: Date.now(),
  };
  sets.push(dup);
  if (!_saveTaskSets()) {
    sets.pop(); // rollback
    return null;
  }
  return _deepCopyTaskSet(dup);
}

/**
 * Delete a task set. If it was active, clears the active id.
 * Returns false if not found or is built-in.
 */
export function deleteTaskSet(id: string): boolean {
  const sets = _initTaskSets();
  const idx = sets.findIndex((s) => s.id === id);
  if (idx === -1) return false;
  if (sets[idx].builtIn) return false;

  const [removed] = sets.splice(idx, 1);
  if (!_saveTaskSets()) {
    sets.splice(idx, 0, removed); // rollback
    return false;
  }

  if (_activeTaskSetId === id) {
    _activeTaskSetId = null;
    _saveActiveTaskSetId();
  }
  return true;
}

/**
 * Compare the current task templates against the active set.
 * Returns true if they differ (set is "dirty").
 */
export function isTaskSetDirty(): boolean {
  const activeId = getActiveTaskSetId();
  if (!activeId) return false;
  const tset = getTaskSetById(activeId);
  if (!tset) return false;
  const currentTemplates = _snapshotCurrentTaskTemplates();
  if (JSON.stringify(currentTemplates) !== JSON.stringify(tset.templates)) return true;
  const currentOts = _snapshotCurrentOneTimeTasks();
  if (JSON.stringify(currentOts) !== JSON.stringify(tset.oneTimeTasks)) return true;
  if (JSON.stringify(_restRules) !== JSON.stringify(tset.restRules || [])) return true;
  return false;
}

// ─── Data Transfer Helpers ──────────────────────────────────────────────────

const ALL_STORAGE_KEYS = [
  STORAGE_KEY_STATE,
  STORAGE_KEY_SCHEDULE,
  STORAGE_KEY_LIVE_MODE,
  STORAGE_KEY_ALGORITHM,
  STORAGE_KEY_PRESETS,
  STORAGE_KEY_ACTIVE_PRESET,
  STORAGE_KEY_SNAPSHOTS,
  STORAGE_KEY_ACTIVE_SNAPSHOT,
  STORAGE_KEY_PSETS,
  STORAGE_KEY_ACTIVE_PSET,
  STORAGE_KEY_TASK_SETS,
  STORAGE_KEY_ACTIVE_TASK_SET,
];

/** Return the 12 localStorage key names used by the app. */
export function getAllStorageKeys(): string[] {
  return [...ALL_STORAGE_KEYS];
}

/**
 * Prepare an incoming TaskSet for storage: coerce any `oneTimeTasks[*].scheduledDate`
 * string back to a Date, and sanitize template/one-time colors at the data-entry
 * chokepoint. Shared by `importTaskSetDirect` (add) and `replaceTaskSetDirect`
 * (atomic replace) so both ingress paths sanitize identically — never skip this on
 * the replace path. Mutates and returns `tset` (the caller's clone).
 */
function _prepareTaskSetForStore(tset: TaskSet): TaskSet {
  // Restore OneTimeTask scheduledDate from string to Date if needed
  for (const ot of tset.oneTimeTasks) {
    if (typeof ot.scheduledDate === 'string') {
      ot.scheduledDate = new Date(ot.scheduledDate as unknown as string);
    }
  }
  _sanitizeTaskSetColors([tset]);
  return tset;
}

/** Import a pre-built TaskSet directly (bypassing snapshot-from-current). */
export function importTaskSetDirect(tset: TaskSet): boolean {
  const sets = _initTaskSets();
  if (sets.length >= MAX_TASK_SETS) return false;
  _prepareTaskSetForStore(tset);
  sets.push(tset);
  if (!_saveTaskSets()) {
    sets.pop(); // rollback
    return false;
  }
  return true;
}

/**
 * Atomically replace an existing task set's content in place. Unlike the old
 * delete-then-import flow, a failed save does NOT lose the previous set: the swap
 * is in-memory and `_saveTaskSets` throws (on quota) BEFORE committing, so
 * restoring `sets[idx]` re-aligns memory with the untouched disk. A pure swap
 * leaves the count unchanged, so `MAX_TASK_SETS` can never be the failure cause
 * here — only quota, which rolls back cleanly. If `replaceId` is missing or points
 * at a built-in set (the UI filters those out of replace targets), this falls back
 * to ADD, preserving the pre-fix net effect of a no-op delete + import.
 */
export function replaceTaskSetDirect(replaceId: string, tset: TaskSet): boolean {
  const sets = _initTaskSets();
  const idx = sets.findIndex((s) => s.id === replaceId);
  if (idx === -1 || sets[idx].builtIn) return importTaskSetDirect(tset);

  const prepared = _prepareTaskSetForStore(tset);
  const prev = sets[idx];
  sets[idx] = prepared;
  if (!_saveTaskSets()) {
    sets[idx] = prev; // rollback — old set restored, no loss
    return false;
  }
  // Mirror deleteTaskSet: replacing the active set deselects it.
  if (_activeTaskSetId === replaceId) {
    _activeTaskSetId = null;
    _saveActiveTaskSetId();
  }
  return true;
}

/** Import a pre-built ParticipantSet directly. */
export function importParticipantSetDirect(pset: ParticipantSet): boolean {
  const sets = _initParticipantSets();
  if (sets.length >= MAX_PARTICIPANT_SETS) return false;
  const normalized = _normalizeParticipantSet(pset);
  sets.push(normalized);
  if (!_saveParticipantSets()) {
    sets.pop(); // rollback
    return false;
  }
  return true;
}

/**
 * Atomically replace an existing participant set's content in place. Same no-loss
 * guarantee as `replaceTaskSetDirect`: the in-memory swap is rolled back if
 * `_saveParticipantSets` throws (on quota) before committing, so the previous set
 * survives. `_normalizeParticipantSet` preserves orphaned cert ids and sanitizes
 * pakalIds/catalogs identically to the add path. Missing/built-in target falls
 * back to ADD.
 */
export function replaceParticipantSetDirect(replaceId: string, pset: ParticipantSet): boolean {
  const sets = _initParticipantSets();
  const idx = sets.findIndex((s) => s.id === replaceId);
  if (idx === -1 || sets[idx].builtIn) return importParticipantSetDirect(pset);

  const normalized = _normalizeParticipantSet(pset);
  const prev = sets[idx];
  sets[idx] = normalized;
  if (!_saveParticipantSets()) {
    sets[idx] = prev; // rollback — old set restored, no loss
    return false;
  }
  // Mirror deleteParticipantSet: replacing the active set deselects it.
  if (_activeParticipantSetId === replaceId) {
    _activeParticipantSetId = null;
    _saveActiveParticipantSetId();
  }
  return true;
}

/** Import a pre-built ScheduleSnapshot directly. */
export function importSnapshotDirect(snap: ScheduleSnapshot): boolean {
  const snapshots = _initSnapshots();
  if (snapshots.length >= MAX_SNAPSHOTS) return false;
  _sanitizeSnapshots([snap]);
  snapshots.push(snap);
  if (!_saveSnapshots()) {
    snapshots.pop(); // rollback
    return false;
  }
  return true;
}

/**
 * Replace current algorithm settings and all user presets in one operation.
 * The built-in Default preset is always preserved.
 */
export function replaceAlgorithmSettingsAndPresets(
  settings: AlgorithmSettings,
  presets: AlgorithmPreset[],
  activeId: string | null,
): boolean {
  // Replace working copy. Merge config over DEFAULT_ALGORITHM_SETTINGS.config so
  // a legacy/cross-version file missing newer keys (e.g. restPerGapWeight,
  // splitPenalty) is healed rather than stored with `undefined` — which would
  // poison the composite score to NaN. Normalize dayStartHour like every other
  // ingress path.
  _algorithmSettings = {
    config: { ...DEFAULT_ALGORITHM_SETTINGS.config, ...settings.config },
    disabledHardConstraints: [...settings.disabledHardConstraints],
    dayStartHour: _normalizeDayStartHour(settings.dayStartHour),
    splittingMode: settings.splittingMode ?? DEFAULT_ALGORITHM_SETTINGS.splittingMode,
  };
  if (!_saveAlgorithmSettings()) return false;

  // Replace presets: keep built-in Default, replace everything else
  const current = _initPresets();
  const builtIn = current.filter((p) => p.builtIn);
  const imported = presets.filter((p) => !p.builtIn);
  _presets = [...builtIn, ...imported];
  // Ensure Default always exists
  if (!_presets.find((p) => p.id === DEFAULT_PRESET.id)) {
    _presets.unshift(_deepCopyPreset(DEFAULT_PRESET));
  }
  if (!_savePresets()) return false;

  // Set active preset
  _activePresetId = activeId && _presets.find((p) => p.id === activeId) ? activeId : DEFAULT_PRESET.id;
  _saveActivePresetId();
  notifyAlgorithmChanged();
  return true;
}

/** Add a single algorithm preset directly. */
export function addAlgorithmPresetDirect(preset: AlgorithmPreset): boolean {
  const presets = _initPresets();
  presets.push(preset);
  if (!_savePresets()) {
    presets.pop(); // rollback
    return false;
  }
  return true;
}
